/**
 * DisciplineMatrix - Reusable matrix component for all progress tracking disciplines
 * 
 * This component replaces the monolithic ProjectDetail.js with a configuration-driven
 * approach. Each discipline (Piping, HVAC, Structural, etc.) uses the same component
 * with different configuration files.
 * 
 * Performance optimizations:
 * - useReducer instead of 80+ useState hooks
 * - React.memo on cell components
 * - Virtual scrolling for large datasets (react-window)
 * - Memoized column definitions
 */

import React, { useReducer, useEffect, useMemo, useCallback, useRef, memo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { List } from 'react-window';

import { authenticatedApiCall, authenticatedApiCallWithRetry, isRetryableLockError } from '../../utils/apiClient';
import { applyColumnOrder, loadColumnOrder } from '../../utils/columnLayout';
import { getDisciplineConfig } from '../../config/disciplines';
import { isFieldRunShop } from '../../utils/pipingFieldRun';
import { matrixReducer, initialState, actions } from './matrixReducer';
import { 
  EditableCell, 
  ProgressCell, 
  DateCell, 
  PriorityCell,
  ColumnFilter,
  FabricationShopCell,
  SelectCell
} from './cells';
import useResizableColumns from '../../hooks/useResizableColumns';
import useMatrixPresence from '../../hooks/useMatrixPresence';
import ActiveViewers from './ActiveViewers';
import ProgressAnalyticsDashboard from './ProgressAnalyticsDashboard';
import MechanicalTableDropdown, { getSubTableById } from './MechanicalTableDropdown';
import PipingTrackerDropdown from './PipingTrackerDropdown';
import SubDisciplineFilter from './SubDisciplineFilter';
import BarcodePrintModal from './BarcodePrintModal';
import NdeAnalytics from '../ProgressTracking/Piping/NdeAnalytics';
import PipeTestingAnalytics from '../ProgressTracking/Piping/PipeTestingAnalytics';
import Toast from '../Layout/Toast';
import ShopFieldRatioModal from '../Estimation/ShopFieldRatioModal';
import ProgressTrackerAuditLogModal from './ProgressTrackerAuditLogModal';
import { ListenButton } from '../common/AudioGuide';
import PipingProgressWalkthrough from '../Help/PipingProgressWalkthrough';
import { PIPING_WALKTHROUGH_GUIDE_ID } from '../Help/helpTours/registry';
import HelpVideoTour from '../Help/HelpVideoTour';
import { PIPING_SUB_DISCIPLINES } from '../../config/disciplines/piping';
import { ELECTRICAL_SUB_DISCIPLINES } from '../../config/disciplines/electrical';
import { TagCell, TagPicker, TagUsageModal, useEntityTags } from '../common/tags';

class AnalyticsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Analytics crashed:', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '500px', textAlign: 'center' }}>
            <h3 style={{ color: '#dc2626', margin: '0 0 1rem' }}>Analytics Error</h3>
            <p style={{ color: '#6b7280', margin: '0 0 1rem' }}>{this.state.error?.message || 'An unexpected error occurred while loading analytics.'}</p>
            <button onClick={this.props.onClose} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>Close</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Get progress style based on value
 */
const getProgressStyle = (progress) => {
  if (progress >= 90) return { backgroundColor: '#d4edda', color: '#155724' };
  if (progress >= 50) return { backgroundColor: '#fff3cd', color: '#856404' };
  if (progress >= 25) return { backgroundColor: '#f8d7da', color: '#721c24' };
  return { backgroundColor: '#f8f9fa', color: '#6c757d' };
};

/** Mechanical unified matrix: sub-tables that support POST /mechanical/move-row */
const MECHANICAL_SUBTABLE_NAMES = new Set([
  'rotating_equipment_tracking',
  'vessels_tracking',
  'chutes_tracking',
  'conveyors_tracking',
  'skids_tracking',
]);

const MECHANICAL_TYPE_TO_TABLE = {
  'Rotating Equipment': 'rotating_equipment_tracking',
  Rotating: 'rotating_equipment_tracking',
  Vessels: 'vessels_tracking',
  Skids: 'skids_tracking',
  Chutes: 'chutes_tracking',
  Conveyors: 'conveyors_tracking',
};

function normalizeMechanicalEquipmentType(raw) {
  if (raw == null || raw === '') return null;
  const exact = String(raw).trim();
  if (MECHANICAL_TYPE_TO_TABLE[exact]) return exact;
  const key = exact.toLowerCase();
  const lowerToCanonical = {
    'rotating equipment': 'Rotating Equipment',
    rotating: 'Rotating Equipment',
    'rotating eqp': 'Rotating Equipment',
    'rotating eqpt': 'Rotating Equipment',
    vessels: 'Vessels',
    vessel: 'Vessels',
    skids: 'Skids',
    skid: 'Skids',
    chutes: 'Chutes',
    chute: 'Chutes',
    conveyors: 'Conveyors',
    conveyor: 'Conveyors',
  };
  return lowerToCanonical[key] || null;
}

/**
 * Row Component for react-window v2.x
 * Defined outside to be stable across renders
 * Props come from rowProps, plus index and style from List
 */
const Row = memo(({ index, style, rows, columns, config, changedRows, renderTableCell, onDragEnter, dragState, hDrag, progressColumnIds, collapsedGroups, selectedRows, onToggleRowSelection, hasEditPermission, hasDeletePermission, columnWidths, disciplineId, highlightedRowId, onDuplicateRow, renderActionColumns = true }) => {
  const row = rows[index];
  if (!row) return null;
  
  const isChanged = changedRows?.has(row.id) || false;
  const isSelected = selectedRows?.has(row.id) || false;
  const isDragActive = dragState.active;
  const isOnHold = row.onHold === true;
  const isSuperseded = row.isSuperseded === true;
  const isAuditHighlighted = highlightedRowId != null && String(row.id) === String(highlightedRowId);
  
  // Determine if this row is part of the drag highlight range
  // Only if drag is active and column matches
  let isHighlighted = false;
  if (isDragActive && dragState.startRow !== null && dragState.currentDragRow !== null) {
    const start = Math.min(dragState.startRow, dragState.currentDragRow);
    const end = Math.max(dragState.startRow, dragState.currentDragRow);
    if (index >= start && index <= end) {
      isHighlighted = true;
    }
  }

  // Horizontal drag-fill highlight: only the source row, across the spanned progress columns
  const hActiveRow = hDrag?.active && hDrag.rowId === row.id;
  let hLo = -1;
  let hHi = -1;
  if (hActiveRow && progressColumnIds) {
    const s = progressColumnIds.indexOf(hDrag.startColId);
    const c = progressColumnIds.indexOf(hDrag.currentColId);
    if (s >= 0 && c >= 0) {
      hLo = Math.min(s, c);
      hHi = Math.max(s, c);
    }
  }

  // Build cells including spacers for collapse buttons
  const cells = [];

  // NDE / Pipe Testing duplicate button per row — first action column (for pt_edit users)
  if (renderActionColumns && (disciplineId === 'piping-nde' || disciplineId === 'piping-testing') && hasEditPermission && onDuplicateRow) {
    cells.push(
      <div
        key="duplicate-btn"
        style={{
          width: 28, minWidth: 28, maxWidth: 28, height: '100%',
          borderRight: '1px solid #dee2e6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicateRow(row.id); }}
          title="Duplicate this row"
          style={{
            width: 22, height: 22, padding: 0, border: '1px solid #d1d5db',
            borderRadius: '4px', cursor: 'pointer', fontSize: '14px', lineHeight: 1,
            backgroundColor: '#fff', color: '#059669', fontWeight: '700',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#059669'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#059669'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#059669'; e.currentTarget.style.borderColor = '#d1d5db'; }}
        >+</button>
      </div>
    );
  }

  // Checkbox cell for row selection (if delete permission)
  if (renderActionColumns && hasDeletePermission) {
    cells.push(
      <div
        key="select-checkbox"
        style={{
          width: 32, minWidth: 32, maxWidth: 32, height: '100%',
          borderRight: '1px solid #dee2e6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxSizing: 'border-box',
          backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleRowSelection && onToggleRowSelection(row.id)}
          style={{ cursor: 'pointer' }}
          title="Select for delete"
        />
      </div>
    );
  }
  
  let lastGroup = null;
  columns.forEach((column) => {
    const isCollapsed = column.headerGroup && collapsedGroups?.[column.headerGroup];
    const isFirstInGroup = column.headerGroup && column.headerGroup !== lastGroup;
    
    // Add spacer for collapse button when starting a new group
    if (isFirstInGroup) {
      cells.push(
        <div
          key={`spacer-${column.headerGroup}`}
          style={{
            width: 18,
            minWidth: 18,
            maxWidth: 18,
            height: '100%',
            borderRight: isCollapsed ? '1px solid #dee2e6' : 'none',
            boxSizing: 'border-box',
          }}
        />
      );
    }
    
    // Skip column if its group is collapsed
    if (isCollapsed) {
      lastGroup = column.headerGroup;
      return;
    }
    
    const value = row[column.accessor];
    const isDragColumn = isDragActive && column.id === dragState.columnId;
    const colWidth = columnWidths?.[column.id] || column.width;
    // Horizontal drag-fill highlight for this cell (source row only)
    let isHCell = false;
    if (hActiveRow && hLo >= 0) {
      const pIdx = progressColumnIds.indexOf(column.id);
      isHCell = pIdx >= hLo && pIdx <= hHi;
    }
    
    cells.push(
      <div
        key={column.id}
        data-col-id={column.id}
        style={{
          width: colWidth,
          minWidth: colWidth,
          maxWidth: colWidth,
          height: '100%',
          borderRight: '1px solid #dee2e6',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: '11px',
          boxSizing: 'border-box',
          backgroundColor: ((isHighlighted && isDragColumn) || isHCell) ? '#e8f0fe' : 'transparent',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          position: 'relative', // Required for absolute positioned children (e.g., EditableCell input)
        }}
        title={value != null ? String(value) : ''}
      >
        {renderTableCell(column, value, row, index, hasEditPermission)}
      </div>
    );
    
    lastGroup = column.headerGroup;
  });

  // Add spacer for scrollbar padding (16px) at the end of each row
  cells.push(
    <div
      key="scrollbar-spacer"
      style={{
        width: 16,
        minWidth: 16,
        maxWidth: 16,
        height: '100%',
        boxSizing: 'border-box',
      }}
    />
  );

  const rowBg = isAuditHighlighted
    ? '#fde68a'
    : isSuperseded
      ? '#eceff1'
      : isOnHold
        ? '#f0e6e6'
        : isChanged ? '#fff3cd' : (index % 2 === 0 ? '#ffffff' : '#f1f3f5');

  return (
    <div 
      style={{
        ...style,
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: rowBg,
        borderBottom: '1px solid #dee2e6',
        opacity: isSuperseded ? 0.6 : (isOnHold ? 0.7 : 1),
        textDecoration: isSuperseded ? 'line-through' : undefined,
        boxShadow: isAuditHighlighted ? 'inset 0 0 0 2px #f59e0b' : undefined,
        transition: 'background-color 0.4s ease',
      }}
      title={isSuperseded ? `Superseded by a newer ISO version${row.supersededAt ? ` on ${new Date(row.supersededAt).toLocaleDateString()}` : ''} — read-only, kept for baseline history.` : undefined}
      onMouseEnter={() => isDragActive && onDragEnter(index)}
    >
      {cells}
    </div>
  );
}, (prev, next) => {
  // Custom equality check for performance
  // IMPORTANT: Compare specific values, not entire objects containing Sets
  // This prevents React Fast Refresh infinite recursion
  return (
    prev.index === next.index && 
    prev.style === next.style &&
    prev.rows === next.rows &&
    prev.columns === next.columns &&
    prev.config === next.config &&
    prev.changedRows === next.changedRows &&
    prev.dragState === next.dragState &&
    prev.hDrag === next.hDrag &&
    prev.progressColumnIds === next.progressColumnIds &&
    prev.collapsedGroups === next.collapsedGroups &&
    prev.selectedRows === next.selectedRows &&
    prev.hasEditPermission === next.hasEditPermission &&
    prev.hasDeletePermission === next.hasDeletePermission &&
    prev.columnWidths === next.columnWidths &&
    prev.disciplineId === next.disciplineId &&
    prev.highlightedRowId === next.highlightedRowId &&
    prev.onDuplicateRow === next.onDuplicateRow &&
    prev.renderActionColumns === next.renderActionColumns
  );
});

/**
 * DisciplineMatrix Component
 */
const DisciplineMatrix = ({ 
  disciplineId,
  projectId: propProjectId,
  projectDetails,
  permissions: propPermissions,
  onLineTagClick,
  onUploadClick,
  onExportClick,
  onDownloadTemplateClick,
  onColumnConfigClick,
  onMatBoreClick,
  onManageTeamClick,
  onManageUsersClick,
  onRulesOfCreditClick,
  columnMappings: propColumnMappings,
  onColumnMappingsChange,
  visibleColumns: propVisibleColumns, // Set of column keys to display
  columnOrder: propColumnOrder,
  refreshKey, // Increment to trigger data refresh (e.g., after upload completes)
  onFilterStateChange, // Callback to report filter state to parent
  // Mechanical sub-table props
  mechanicalSubTableId, // Current sub-table ID (e.g., 'rotating-equipment', 'vessels')
  onMechanicalSubTableChange, // Callback when sub-table changes
  // Piping sub-view props
  pipingSubViewId, // Current piping sub-view ID (e.g., 'progress-tracker', 'nde-tracker', 'welder-log')
  onPipingSubViewChange, // Callback when piping sub-view changes
  // NDE-specific props
  onAuditLogClick, // Callback to open audit log modal
  onNdeImportClick, // Callback to open NDE import modal
}) => {
  // Use prop projectId if provided, otherwise get from URL params
  const { projectId: urlProjectId } = useParams();
  const projectId = propProjectId || urlProjectId;
  const { instance, accounts } = useMsal();
  
  // Get discipline configuration
  const config = useMemo(() => {
    const c = getDisciplineConfig(disciplineId);
    console.log('Config features:', c.features);
    return c;
  }, [disciplineId]);

  // NDE and Pipe Testing are hard-delete "sub-trackers" that share the row
  // duplicate feature (copies auto-populated/spec fields only), their own
  // audit log, and dedicated analytics dashboards.
  const hasDuplicateFeature = disciplineId === 'piping-nde' || disciplineId === 'piping-testing';
  const hasOwnAuditLog = disciplineId === 'piping-nde' || disciplineId === 'piping-testing';
  
  // Live data push: ref + callback for real-time sync
  const fetchMatrixDataRef = useRef(null);
  const processedDataRef = useRef([]);
  const handleDataChange = useCallback(() => {
    if (fetchMatrixDataRef.current) fetchMatrixDataRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this matrix)
  // Only enabled if the discipline config has presence feature enabled
  const presenceEnabled = config.features?.presence === true;
  const { 
    viewers: presenceViewers, 
    isConnected: presenceConnected, 
    connectionState: presenceConnectionState,
    socketId,
  } = useMatrixPresence(projectId, disciplineId, presenceEnabled, handleDataChange);

  // Socket ID tracking for sync headers
  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // Internal column mappings state (used when parent doesn't provide propColumnMappings)
  const [internalColumnMappings, setInternalColumnMappings] = useState([]);
  
  useEffect(() => {
    if (propColumnMappings) return; // Parent manages mappings
    if (!projectId || !disciplineId) return;
    const fetchMappings = async () => {
      try {
        const apiEndpoint = disciplineId === 'piping'
          ? `/matrix/project/${projectId}/column-mappings`
          : `/discipline-matrix/${disciplineId}/project/${projectId}/column-mappings`;
        const response = await authenticatedApiCall(instance, accounts, 'GET', apiEndpoint);
        if (response.data.success) {
          setInternalColumnMappings(response.data.data || []);
        }
      } catch (err) {
        // Column mappings are optional - fail silently
      }
    };
    fetchMappings();
  }, [projectId, disciplineId, propColumnMappings, instance, accounts, refreshKey]);

  const effectiveColumnMappings = propColumnMappings || internalColumnMappings;

  // Rules of Credit freeze state for this discipline + job. Progress entry is locked
  // until the RoC are frozen (superadmin bypasses). Refetched when RoC is saved/frozen
  // (refreshKey bump from the parent after the modal closes).
  const rocJobNumber = projectDetails?.job_number;
  const [rocFrozen, setRocFrozen] = useState(false);
  const [rocCanFreeze, setRocCanFreeze] = useState(false);
  const [rocChecked, setRocChecked] = useState(false);

  useEffect(() => {
    if (!disciplineId || !rocJobNumber) { setRocChecked(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedApiCall(instance, accounts, 'GET', `/rules-of-credit/${disciplineId}/${rocJobNumber}`);
        if (!cancelled && resp.data && resp.data.success) {
          setRocFrozen(resp.data.data?.is_frozen === true);
          setRocCanFreeze(resp.data.canFreeze === true);
        }
      } catch (e) {
        // Fail open on error so a lookup failure never hard-blocks the UI
        if (!cancelled) setRocFrozen(true);
      } finally {
        if (!cancelled) setRocChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [disciplineId, rocJobNumber, instance, accounts, refreshKey]);

  // Merge dynamic UDF columns from project column mappings into config.columns
  // Filter by showInDashboard - only show columns marked for dashboard display
  // Also filter by user visibility preferences if provided
  const allColumns = useMemo(() => {
    // Start with columns that are either default-visible or explicitly enabled by user
    let dashboardColumns = config.columns.filter(col => {
      const colKey = col.id || col.accessor;
      const isDefaultVisible = col.showInDashboard === undefined || col.showInDashboard === true;
      const isUserEnabled = propVisibleColumns && propVisibleColumns.size > 0 && propVisibleColumns.has(colKey);
      return isDefaultVisible || isUserEnabled;
    });
    
    // Add UDF columns from project column mappings
    if (effectiveColumnMappings && effectiveColumnMappings.length > 0) {
      effectiveColumnMappings.forEach(mapping => {
        // Check if column not already in base config
        if (!dashboardColumns.find(c => (c.id || c.accessor) === mapping.column_key)) {
          dashboardColumns.push({
            id: mapping.column_key,
            accessor: mapping.column_key,
            header: mapping.custom_name,
            type: 'text',
            editable: mapping.is_editable !== false,
            filterable: true,
            width: 120,
          });
        }
      });
    }
    
    // Filter by user visibility preferences if provided
    if (propVisibleColumns && propVisibleColumns.size > 0) {
      dashboardColumns = dashboardColumns.filter(col => {
        const colKey = col.id || col.accessor;
        return propVisibleColumns.has(colKey);
      });
    }

    const storedOrder = propColumnOrder?.length
      ? propColumnOrder
      : (projectId && disciplineId ? loadColumnOrder(`${disciplineId}_${projectId}_columnOrder`) : null);
    if (storedOrder?.length) {
      dashboardColumns = applyColumnOrder(dashboardColumns, storedOrder, (col) => col.id || col.accessor);
    }
    
    return dashboardColumns;
  }, [config.columns, effectiveColumnMappings, propVisibleColumns, propColumnOrder, projectId, disciplineId, refreshKey]);

  // ============================================
  // Frozen column configuration
  // ============================================
  // Default behaviour: a single leading column is frozen (the first visible column)
  // and the +/checkbox action columns live in the scrollable row.
  // A discipline config may opt into freezing multiple leading columns by setting
  // `frozenColumnIds` (array of column ids in pinned order). Setting
  // `freezeActionColumns: true` additionally pins the +/checkbox action columns.
  const freezeActionColumns = config.freezeActionColumns === true;

  // Ordered list of column ids to pin (falls back to the first visible column)
  const frozenColumnIds = useMemo(() => {
    if (Array.isArray(config.frozenColumnIds) && config.frozenColumnIds.length > 0) {
      const present = new Set(allColumns.map(c => c.id));
      const ids = config.frozenColumnIds.filter(id => present.has(id));
      if (ids.length > 0) return ids;
    }
    return allColumns[0] ? [allColumns[0].id] : [];
  }, [config.frozenColumnIds, allColumns]);

  // Pinned column objects, in pinned order
  const frozenColumns = useMemo(() => {
    const byId = new Map(allColumns.map(c => [c.id, c]));
    return frozenColumnIds.map(id => byId.get(id)).filter(Boolean);
  }, [allColumns, frozenColumnIds]);

  // Remaining columns rendered in the scrollable (virtualized) area
  const scrollColumns = useMemo(() => {
    const frozenSet = new Set(frozenColumnIds);
    return allColumns.filter(c => !frozenSet.has(c.id));
  }, [allColumns, frozenColumnIds]);

  // localStorage key for collapsed groups - unique per project and discipline
  const collapsedGroupsKey = useMemo(() => {
    if (!projectId || !disciplineId) return null;
    return `matrix_collapsed_groups_${projectId}_${disciplineId}`;
  }, [projectId, disciplineId]);
  
  // Helper to load collapsed groups from localStorage
  const loadCollapsedGroups = useCallback(() => {
    if (!collapsedGroupsKey) return {};
    try {
      const stored = localStorage.getItem(collapsedGroupsKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to load collapsed groups from localStorage:', e);
    }
    return {};
  }, [collapsedGroupsKey]);
  
  // Helper to save collapsed groups to localStorage
  const saveCollapsedGroups = useCallback((groups) => {
    if (!collapsedGroupsKey) return;
    try {
      localStorage.setItem(collapsedGroupsKey, JSON.stringify(groups));
    } catch (e) {
      console.warn('Failed to save collapsed groups to localStorage:', e);
    }
  }, [collapsedGroupsKey]);
  
  // Consolidated state via useReducer
  const [state, dispatch] = useReducer(matrixReducer, initialState);

  // ============================================
  // Universal tagging (per-row tags, bulk tagging, filter-by-tag)
  // ============================================
  // Enabled for disciplines with a registered tag entity type (see
  // config/disciplines/index.js). Tags load in one batched request per page
  // of rows via useEntityTags; assign/remove is optimistic.
  const tagEntityType = config.tagEntityType || null;
  const numericProjectId = projectId ? parseInt(projectId, 10) : null;
  const tagRowIds = useMemo(
    () => (tagEntityType ? state.data.rows.map((r) => r.id) : []),
    [tagEntityType, state.data.rows]
  );
  const entityTags = useEntityTags(tagEntityType, tagRowIds, {
    projectId: numericProjectId,
    enabled: !!tagEntityType,
  });
  const { tagsByEntity, assign: assignEntityTag, unassign: unassignEntityTag } = entityTags;

  // Bulk-tag popover (toolbar) + filter-by-tag state.
  // ?tag=<id> in the URL (links from the global tag explorer) preselects the filter.
  const initialUrlTagId = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get('tag');
    const id = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(id) ? id : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [bulkTagAnchor, setBulkTagAnchor] = useState(null); // DOMRect | null
  const [tagFilterAnchor, setTagFilterAnchor] = useState(null); // DOMRect | null
  const [tagFilterIds, setTagFilterIds] = useState(initialUrlTagId != null ? [initialUrlTagId] : []);
  const [usageTagId, setUsageTagId] = useState(null); // global explorer modal

  const isFirstTagReset = useRef(true);
  useEffect(() => {
    // Skip the mount pass so the ?tag= deep link survives; reset on real switches.
    if (isFirstTagReset.current) { isFirstTagReset.current = false; return; }
    setTagFilterIds([]);
    setBulkTagAnchor(null);
    setTagFilterAnchor(null);
  }, [disciplineId, projectId]);

  // Tags common to ALL selected rows — shown as checked in the bulk-tag picker
  const bulkSelectedTags = useMemo(() => {
    if (!tagEntityType || state.ui.selectedRows.size === 0) return [];
    let common = null;
    for (const rowId of state.ui.selectedRows) {
      const rowTags = tagsByEntity[String(rowId)] || [];
      if (common === null) {
        common = rowTags;
      } else {
        const idSet = new Set(rowTags.map((t) => t.id));
        common = common.filter((t) => idSet.has(t.id));
      }
      if (common.length === 0) break;
    }
    return common || [];
  }, [tagEntityType, state.ui.selectedRows, tagsByEntity]);
  
  // Local drag state (for performance)
  const [dragState, setDragState] = useState({
    active: false,
    startRow: null,
    currentDragRow: null,
    columnId: null,
    startValue: null
  });

  // Horizontal drag-fill state (fill a value across progress-step columns within one row)
  const [hDrag, setHDrag] = useState({
    active: false,
    rowId: null,
    startColId: null,
    currentColId: null,
    startValue: null
  });

  // Dynamic options for select columns (loaded from API)
  const [dynamicOptions, setDynamicOptions] = useState({});

  useEffect(() => {
    const loadDynamicOptions = async () => {
      if (!projectId) return;
      const dynamicCols = config.columns.filter(col => col.dynamicOptionsUrl);
      for (const col of dynamicCols) {
        try {
          const url = col.dynamicOptionsUrl.replace('{projectId}', projectId);
          const response = await authenticatedApiCall(instance, accounts, 'GET', url);
          if (response.data.success) {
            const rows = response.data.data?.rows || response.data.data || [];
            const key = col.dynamicOptionsKey || 'id';
            const opts = ['', ...new Set(rows.map(r => r[key]).filter(Boolean))];
            setDynamicOptions(prev => ({ ...prev, [col.id]: opts }));
          }
        } catch (err) {
          console.warn(`Failed to load dynamic options for ${col.id}:`, err.message);
        }
      }
    };
    loadDynamicOptions();
  }, [config.columns, instance, accounts, projectId]);

  // Column resize - using reusable hook for performant resizing
  // Note: No React state changes during resize - all DOM manipulation for max performance
  const {
    columnWidths,
    tableContainerRef,
    handleResizeStart,
    getColumnWidth,
    resetColumnWidths,
    setColumnWidth,
  } = useResizableColumns(`matrix_column_widths_${propProjectId}_${disciplineId}`);

  // Ref for frozen column scroll sync
  const frozenColumnRef = useRef(null);
  const mainListRef = useRef(null);
  const dragAutoScrollRef = useRef(null); // requestAnimationFrame ID for auto-scroll during drag
  const dragStateRef = useRef(dragState); // stable ref for drag state in mousemove handler
  dragStateRef.current = dragState;
  const hDragRef = useRef(hDrag); // stable ref for horizontal drag in window handlers
  hDragRef.current = hDrag;
  
  // Refs for stable callbacks
  const stateRef = useRef(state);
  const configRef = useRef(config);
  
  // Keep refs in sync
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Load collapsed groups from localStorage on mount/project change
  useEffect(() => {
    if (collapsedGroupsKey) {
      const savedGroups = loadCollapsedGroups();
      if (Object.keys(savedGroups).length > 0) {
        dispatch(actions.setCollapsedGroups(savedGroups));
      }
    }
  }, [collapsedGroupsKey, loadCollapsedGroups]);
  
  // Save collapsed groups to localStorage whenever they change
  useEffect(() => {
    if (collapsedGroupsKey && state.ui.collapsedGroups) {
      saveCollapsedGroups(state.ui.collapsedGroups);
    }
  }, [collapsedGroupsKey, state.ui.collapsedGroups, saveCollapsedGroups]);

  // ============================================
  // Data Loading
  // ============================================
  
  // Soft-delete view scope (superadmin only): 'active' | 'deleted' | 'all'.
  // Declared before fetchMatrixData because that callback's dependency array
  // references it (declaring it later triggers a temporal-dead-zone
  // "Cannot access before initialization" crash on mount).
  const [deletedView, setDeletedView] = useState('active');

  const fetchMatrixData = useCallback(async () => {
    dispatch(actions.setLoading('data', true));
    try {
      let endpoint = config.api.getData.replace('{projectId}', projectId);
      // Superadmin "Show deleted" scope. Backend defaults to active for everyone else.
      if (deletedView && deletedView !== 'active') {
        endpoint += (endpoint.includes('?') ? '&' : '?') + `deleted=${deletedView}`;
      }
      const response = await authenticatedApiCall(instance, accounts, 'GET', endpoint);
      
      if (response.data.success) {
        const transformedData = config.transformData(response.data.data);
        dispatch(actions.setRows(transformedData));
        dispatch(actions.setOriginalData(transformedData));
        dispatch({ type: 'SET_UI', payload: { hasLoadedOnce: true } });
      }
    } catch (error) {
      console.error('Error fetching matrix data:', error);
      dispatch(actions.showToast('Failed to load matrix data', 'error'));
    } finally {
      dispatch(actions.setLoading('data', false));
    }
  }, [config, projectId, instance, accounts, deletedView]);

  useEffect(() => { fetchMatrixDataRef.current = fetchMatrixData; }, [fetchMatrixData]);

  const fetchPriorities = useCallback(async () => {
    try {
      const endpoint = config.api.priorities.replace('{projectId}', projectId);
      const response = await authenticatedApiCall(instance, accounts, 'GET', endpoint);
      if (response.data.success) {
        dispatch(actions.setPriorities(response.data.data));
      }
    } catch (error) {
      console.error('Error fetching priorities:', error);
    }
  }, [config, projectId, instance, accounts]);

  const fetchFabricationLocations = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/fabrication-locations/list');
      if (response.data.success) {
        dispatch(actions.setFabricationLocations(response.data.data));
      }
    } catch (error) {
      console.error('Error fetching fabrication locations:', error);
    }
  }, [instance, accounts]);

  // Callback for when a new fabrication location is added on-the-fly
  const handleLocationAdded = useCallback((newLocation) => {
    // Add the new location to the existing list
    dispatch(actions.setFabricationLocations([
      ...state.data.fabricationLocations,
      newLocation
    ].sort((a, b) => a.location_name.localeCompare(b.location_name))));
  }, [state.data.fabricationLocations]);

  const handleSelectOptionAdded = useCallback(async (columnId, newTypeName) => {
    setDynamicOptions(prev => {
      const existing = prev[columnId] || [];
      if (existing.includes(newTypeName)) return prev;
      return { ...prev, [columnId]: [...existing, newTypeName].sort((a, b) => {
        if (!a) return -1;
        if (!b) return 1;
        return a.localeCompare(b);
      })};
    });
    const col = config.columns.find(c => c.id === columnId);
    if (col?.dynamicOptionsUrl && projectId) {
      try {
        const postUrl = col.dynamicOptionsUrl.replace('{projectId}', projectId);
        await authenticatedApiCall(instance, accounts, 'POST', postUrl, {
          typeName: newTypeName,
          discipline: disciplineId,
        });
      } catch (err) {
        console.warn(`Failed to persist new option for ${columnId}:`, err.message);
      }
    }
  }, [config.columns, projectId, disciplineId, instance, accounts]);

  const checkPermissions = useCallback(async () => {
    dispatch(actions.setPermission('loading', true));
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
      if (response.data.success) {
        const userPermissions = response.data.data.permissions || [];
        const isAdmin = response.data.data.isAdmin || false;
        const isSuperadmin = response.data.data.isSuperadmin || false;
        console.log('Permissions loaded:', { isAdmin, isSuperadmin, userPermissions });
        // pt_* permissions from config.permissions
        dispatch(actions.setPermissions({
          edit: isAdmin || userPermissions.includes(config.permissions.edit),
          editAll: isAdmin || (config.permissions.editAll && userPermissions.includes(config.permissions.editAll)),
          delete: isAdmin || userPermissions.includes(config.permissions.delete),
          analytics: isAdmin || userPermissions.includes(config.permissions.analytics),
          import: isAdmin || userPermissions.includes(config.permissions.import),
          export: isAdmin || userPermissions.includes(config.permissions.export),
          template: config.permissions.template ? (isAdmin || userPermissions.includes(config.permissions.template)) : false,
          columnConfig: isAdmin || userPermissions.includes(config.permissions.columnConfig),
          roc: config.permissions.roc ? (isAdmin || userPermissions.includes(config.permissions.roc)) : false,
          sfRatio: config.permissions.sfRatio ? (isAdmin || userPermissions.includes(config.permissions.sfRatio)) : false,
          matBore: config.permissions.matBore ? (isAdmin || userPermissions.includes(config.permissions.matBore)) : false,
          isAdmin: isAdmin,
          isSuperadmin: isSuperadmin,
          loading: false,
        }));
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
      dispatch(actions.setPermission('loading', false));
    }
  }, [config, instance, accounts]);

  useEffect(() => {
    if (projectId && config) {
      checkPermissions();
      fetchMatrixData();
      fetchPriorities();
      fetchFabricationLocations();
    }
  }, [projectId, config, checkPermissions, fetchMatrixData, fetchPriorities, fetchFabricationLocations]);

  // Refresh data when refreshKey changes (e.g., after upload job completes)
  useEffect(() => {
    if (refreshKey > 0 && projectId && config) {
      console.log('Refreshing matrix data due to refreshKey change:', refreshKey);
      fetchMatrixData();
    }
  }, [refreshKey, projectId, config, fetchMatrixData]);

  // ============================================
  // Cell Update Handlers
  // ============================================
  
  const handleCellUpdate = useCallback((rowId, columnId, value) => {
    // Update row by ID (handles filtered/sorted data correctly)
    dispatch(actions.updateRowById(rowId, columnId, value));
    
    // Mark row as changed - backend will recalculate totalFieldCompletion if needed
    dispatch(actions.addChangedRow(rowId));
  }, []);

  // Track rows that are currently retrying for UI feedback
  const [retryingRows, setRetryingRows] = useState(new Set());
  
  // Track rows that failed to save (for beforeunload warning)
  const [failedSaveRows, setFailedSaveRows] = useState(new Set());
  
  // Warn user before leaving if there are failed saves
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (failedSaveRows.size > 0) {
        e.preventDefault();
        e.returnValue = 'Some changes failed to save. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [failedSaveRows]);
  
  // Shop/Field Ratio Modal state (for demo features)
  const [showShopFieldRatioModal, setShowShopFieldRatioModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showBarcodePrint, setShowBarcodePrint] = useState(false);
  const [showProgressAudit, setShowProgressAudit] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [helpTab, setHelpTab] = useState('guide');

  const handleCellSave = useCallback(async (rowId, columnId, oldValue, newValue) => {
    try {
      // Plan 11: superseded ISO-version lines are read-only history. Block edits.
      const targetRow = state.data.rows.find(r => String(r.id) === String(rowId));
      if (targetRow && targetRow.isSuperseded) {
        dispatch(actions.showToast('This line was superseded by a newer ISO version and is read-only (kept for baseline history).', 'error'));
        dispatch(actions.removeChangedRow(rowId));
        fetchMatrixData();
        return;
      }
      // Type change on mechanical sub-tables = move row between DB tables (not just equipment_type text)
      const isMechanicalSubtable = config.tableName && MECHANICAL_SUBTABLE_NAMES.has(config.tableName);
      const isEquipmentTypeCol =
        isMechanicalSubtable &&
        (columnId === 'equipmentType' || columnId === 'equipment_type');

      if (isEquipmentTypeCol && newValue !== oldValue) {
        const canonical = normalizeMechanicalEquipmentType(newValue);
        if (!canonical) {
          dispatch(actions.showToast(
            `Unknown Type "${newValue}". Use: Rotating Equipment, Skids, Vessels, Chutes, or Conveyors.`,
            'error'
          ));
          dispatch(actions.removeChangedRow(rowId));
          fetchMatrixData();
          return;
        }
        const targetTable = MECHANICAL_TYPE_TO_TABLE[canonical];
        if (targetTable && targetTable !== config.tableName) {
          const moveRes = await authenticatedApiCallWithRetry(
            instance, accounts, 'POST', '/mechanical/move-row',
            { rowId, sourceTable: config.tableName, targetType: canonical },
            syncHeaders()
          );
          if (moveRes.data.success) {
            dispatch(actions.showToast(`Row moved to ${canonical}`, 'success'));
            dispatch(actions.removeChangedRow(rowId));
            fetchMatrixData();
            return;
          }
          dispatch(actions.showToast(
            moveRes.data?.message || 'Failed to move row — data was not changed',
            'error'
          ));
          dispatch(actions.removeChangedRow(rowId));
          fetchMatrixData();
          return;
        }
        // Same target table as current view: fall through and persist type text only
      }

      const payload = config.buildChangePayload(rowId, columnId, oldValue, newValue);
      if (!payload) {
        console.warn(`No field mapping for column ${columnId}, skipping save`);
        dispatch(actions.removeChangedRow(rowId));
        return;
      }
      
      // Use retry-enabled API call for lock conflict handling
      const response = await authenticatedApiCallWithRetry(
        instance, 
        accounts, 
        'POST', 
        config.api.saveChanges, 
        {
          project_id: projectId,
          changes: [payload],
        },
        syncHeaders(),
        {
          maxRetries: 3,
          baseDelayMs: 500,
          maxDelayMs: 3000,
          onRetry: (attempt, error, delay) => {
            // Add row to retrying set for UI feedback
            setRetryingRows(prev => new Set([...prev, rowId]));
            dispatch(actions.showToast(`Saving... (retry ${attempt}/3)`, 'info'));
          }
        }
      );
      
      // Remove from retrying set on success
      setRetryingRows(prev => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      
      // Clear from failed saves on success (user retried and it worked)
      setFailedSaveRows(prev => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      
      // Update totalFieldCompletion from backend response if available
      // Response shape differs per backend family:
      //  - Mechanical family: { data: { updatedRows: [{ id, total_field_completion }] } }
      //  - Unified discipline-matrix family: { data: [ {...full row incl total_field_completion} ] }
      const respData = response.data?.data;
      const savedRows = Array.isArray(respData?.updatedRows)
        ? respData.updatedRows
        : (Array.isArray(respData) ? respData : []);
      savedRows.forEach(updatedRow => {
        // Backend returns total_field_completion (DB GENERATED column); accept legacy key too
        const newTotal = updatedRow.total_field_completion ?? updatedRow.field_total_completion;
        if (newTotal !== undefined && newTotal !== null) {
          dispatch(actions.updateRowById(
            updatedRow.id,
            'totalFieldCompletion',
            Math.round(newTotal)
          ));
        }
      });
      
      dispatch(actions.removeChangedRow(rowId));
    } catch (error) {
      // Remove from retrying set on failure
      setRetryingRows(prev => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      
      // Track failed saves for beforeunload warning
      setFailedSaveRows(prev => new Set([...prev, rowId]));
      
      // Still remove from changedRows so dropdown doesn't get stuck disabled
      dispatch(actions.removeChangedRow(rowId));
      
      console.error('Error saving cell change:', error);
      
      // Show appropriate error message
      if (error._retryMetadata?.retriesExhausted) {
        dispatch(actions.showToast('Unable to save - please try again in a moment', 'error'));
      } else {
        dispatch(actions.showToast('Failed to save change', 'error'));
      }
      throw error;
    }
  }, [config, projectId, instance, accounts, fetchMatrixData, dispatch, syncHeaders, state.data.rows]);

  const handleBatchSave = useCallback(async (changes, rowIds) => {
    try {
      if (!changes || changes.length === 0) return;
      
      // Add all rows to retrying set for UI feedback during retry
      setRetryingRows(prev => new Set([...prev, ...rowIds]));
      
      // Use retry-enabled API call for lock conflict handling
      const response = await authenticatedApiCallWithRetry(
        instance, 
        accounts, 
        'POST', 
        config.api.saveChanges, 
        {
          project_id: projectId,
          changes,
        },
        syncHeaders(),
        {
          maxRetries: 3,
          baseDelayMs: 500,
          maxDelayMs: 3000,
          onRetry: (attempt, error, delay) => {
            dispatch(actions.showToast(`Saving batch... (retry ${attempt}/3)`, 'info'));
          }
        }
      );
      
      // Remove all rows from retrying set on success
      setRetryingRows(prev => {
        const next = new Set(prev);
        rowIds.forEach(id => next.delete(id));
        return next;
      });
      
      // Clear from failed saves on success (user retried and it worked)
      setFailedSaveRows(prev => {
        const next = new Set(prev);
        rowIds.forEach(id => next.delete(id));
        return next;
      });
      
      // Update totalFieldCompletion from backend response if available
      // Response shape differs per backend family:
      //  - Mechanical family: { data: { updatedRows: [{ id, total_field_completion }] } }
      //  - Unified discipline-matrix family: { data: [ {...full row incl total_field_completion} ] }
      const respData = response.data?.data;
      const savedRows = Array.isArray(respData?.updatedRows)
        ? respData.updatedRows
        : (Array.isArray(respData) ? respData : []);
      savedRows.forEach(updatedRow => {
        // Backend returns total_field_completion (DB GENERATED column); accept legacy key too
        const newTotal = updatedRow.total_field_completion ?? updatedRow.field_total_completion;
        if (newTotal !== undefined && newTotal !== null) {
          dispatch(actions.updateRowById(
            updatedRow.id,
            'totalFieldCompletion',
            Math.round(newTotal)
          ));
        }
      });
      
      // Remove all affected rows from changed set
      if (rowIds && rowIds.length > 0) {
        dispatch(actions.removeChangedRows(rowIds));
      }
    } catch (error) {
      // Remove all rows from retrying set on failure
      setRetryingRows(prev => {
        const next = new Set(prev);
        rowIds.forEach(id => next.delete(id));
        return next;
      });
      
      // Track failed saves for beforeunload warning
      setFailedSaveRows(prev => new Set([...prev, ...rowIds]));
      
      // Clear from changedRows so the "unsaved changes" badge doesn't get stuck
      if (rowIds && rowIds.length > 0) {
        dispatch(actions.removeChangedRows(rowIds));
      }
      
      console.error('Error saving batch changes:', error);
      
      // Show appropriate error message
      if (error._retryMetadata?.retriesExhausted) {
        dispatch(actions.showToast('Fill-down failed to save — another user may be editing the same rows. Please refresh and try again.', 'error'));
      } else {
        dispatch(actions.showToast('Fill-down failed to save. Please refresh the page and try again.', 'error'));
      }
    }
  }, [config, projectId, instance, accounts]);

  // ============================================
  // Delete / Restore / Purge Handlers (soft-delete aware)
  // ============================================

  // Resolve the mutation base for this discipline (e.g. '/matrix', '/mechanical/vessels',
  // '/discipline-matrix/hvac') from its save-changes endpoint so batch delete/restore/purge
  // hit the correct discipline route instead of always falling back to piping's /matrix.
  const mutationBase = useMemo(() => {
    if (config.api && config.api.saveChanges) {
      return config.api.saveChanges.replace(/\/save-changes$/, '');
    }
    return '/matrix';
  }, [config]);

  // NDE is a hard-delete sub-tracker (not one of the soft-delete discipline tables).
  const isHardDeleteTracker = !!(config.api && config.api.deleteRecords);

  const handleBatchDelete = useCallback(async () => {
    const selectedIds = Array.from(state.ui.selectedRows);
    if (selectedIds.length === 0) {
      dispatch(actions.showToast('No rows selected', 'warning'));
      return;
    }

    const confirmMsg = isHardDeleteTracker
      ? `⚠️ DELETE ${selectedIds.length} ROW(S)\n\nThis permanently deletes the selected row(s). This action cannot be undone.`
      : `Delete ${selectedIds.length} selected row(s)?\n\nThey will be moved to Deleted (hidden from the tracker) and can be restored by a superadmin.`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const deleteUrl = isHardDeleteTracker ? config.api.deleteRecords : `${mutationBase}/delete-batch`;
      const method = isHardDeleteTracker ? 'DELETE' : 'POST';
      const response = await authenticatedApiCall(
        instance,
        accounts,
        method,
        deleteUrl,
        { ids: selectedIds, projectId },
        syncHeaders()
      );

      if (response.data.success) {
        dispatch(actions.removeRows(selectedIds));
        dispatch(actions.clearSelection());
        dispatch(actions.showToast(`${response.data.data?.deletedCount || response.data.deletedCount || selectedIds.length} row(s) deleted successfully`, 'success'));
      } else {
        throw new Error(response.data.message || 'Batch delete failed');
      }
    } catch (error) {
      console.error('Error batch deleting rows:', error);
      dispatch(actions.showToast(`Failed to delete rows: ${error.message}`, 'error'));
    }
  }, [instance, accounts, state.ui.selectedRows, isHardDeleteTracker, config, mutationBase, projectId]);

  // Restore soft-deleted rows (superadmin only). Refetches in the current view so the
  // restored rows leave the "deleted" list.
  const handleRestoreSelected = useCallback(async () => {
    const selectedIds = Array.from(state.ui.selectedRows);
    if (selectedIds.length === 0) {
      dispatch(actions.showToast('No rows selected', 'warning'));
      return;
    }
    if (!window.confirm(`Restore ${selectedIds.length} row(s) back into the tracker?`)) {
      return;
    }
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'POST', `${mutationBase}/restore-batch`,
        { ids: selectedIds, projectId }, syncHeaders()
      );
      if (response.data.success) {
        dispatch(actions.clearSelection());
        dispatch(actions.showToast(`${response.data.data?.restoredCount ?? selectedIds.length} row(s) restored successfully`, 'success'));
        fetchMatrixData();
      } else {
        throw new Error(response.data.message || 'Restore failed');
      }
    } catch (error) {
      console.error('Error restoring rows:', error);
      dispatch(actions.showToast(`Failed to restore rows: ${error.message}`, 'error'));
    }
  }, [instance, accounts, state.ui.selectedRows, mutationBase, projectId, fetchMatrixData]);

  // Permanently delete rows (superadmin only) - irreversible hard delete.
  const handlePurgeSelected = useCallback(async () => {
    const selectedIds = Array.from(state.ui.selectedRows);
    if (selectedIds.length === 0) {
      dispatch(actions.showToast('No rows selected', 'warning'));
      return;
    }
    if (!window.confirm(`⚠️ PERMANENTLY DELETE ${selectedIds.length} ROW(S)\n\nThis cannot be undone and the rows will NOT be recoverable. Continue?`)) {
      return;
    }
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'POST', `${mutationBase}/purge-batch`,
        { ids: selectedIds, projectId }, syncHeaders()
      );
      if (response.data.success) {
        dispatch(actions.removeRows(selectedIds));
        dispatch(actions.clearSelection());
        dispatch(actions.showToast(`${response.data.data?.purgedCount ?? selectedIds.length} row(s) permanently deleted`, 'success'));
      } else {
        throw new Error(response.data.message || 'Purge failed');
      }
    } catch (error) {
      console.error('Error purging rows:', error);
      dispatch(actions.showToast(`Failed to purge rows: ${error.message}`, 'error'));
    }
  }, [instance, accounts, state.ui.selectedRows, mutationBase, projectId]);

  // ============================================
  // Duplicate Handler (NDE tracker - copies auto-populated fields only)
  // ============================================

  const handleDuplicateRows = useCallback(async () => {
    const selectedIds = Array.from(state.ui.selectedRows);
    if (selectedIds.length === 0) {
      dispatch(actions.showToast('No rows selected', 'warning'));
      return;
    }

    try {
      const duplicateUrl = (config.api && config.api.duplicateRecords) || '/nde/duplicate-records';
      const response = await authenticatedApiCall(instance, accounts, 'POST', duplicateUrl, {
        ids: selectedIds,
        projectId,
      });

      if (response.data.success) {
        dispatch(actions.clearSelection());
        const count = response.data.data?.duplicatedCount || 0;
        if (count === 0) {
          dispatch(actions.showToast('No rows were duplicated', 'warning'));
          return;
        }
        dispatch(actions.showToast(`${count} row(s) duplicated successfully`, 'success'));
        // Refresh data to show new rows (without loading state to avoid UI flicker)
        try {
          const dataUrl = config.api.getData.replace('{projectId}', projectId);
          const dataResponse = await authenticatedApiCall(instance, accounts, 'GET', dataUrl);
          if (dataResponse.data.success) {
            const transformed = config.transformData(dataResponse.data.data || dataResponse.data.rows || []);
            dispatch(actions.setRows(transformed));
            dispatch(actions.setOriginalData(transformed));
          }
        } catch (refreshErr) {
          console.warn('Data refresh after duplicate failed:', refreshErr.message);
        }
      } else {
        throw new Error(response.data.message || 'Duplicate failed');
      }
    } catch (error) {
      console.error('Error duplicating rows:', error);
      dispatch(actions.showToast(`Failed to duplicate rows: ${error.message}`, 'error'));
    }
  }, [instance, accounts, state.ui.selectedRows, projectId, config]);

  const handleToggleRowSelection = useCallback((rowId) => {
    dispatch(actions.toggleRowSelection(rowId));
  }, []);

  // Single-row duplicate for NDE tracker (uses same backend endpoint with one ID)
  const handleDuplicateSingleRow = useCallback(async (rowId) => {
    try {
      const duplicateUrl = (config.api && config.api.duplicateRecords) || '/nde/duplicate-records';
      const response = await authenticatedApiCall(instance, accounts, 'POST', duplicateUrl, {
        ids: [rowId],
        projectId,
      });
      if (response.data.success) {
        const count = response.data.data?.duplicatedCount || 0;
        if (count === 0) {
          dispatch(actions.showToast('Row was not duplicated', 'warning'));
          return;
        }
        dispatch(actions.showToast('Row duplicated successfully', 'success'));
        try {
          const dataUrl = config.api.getData.replace('{projectId}', projectId);
          const dataResponse = await authenticatedApiCall(instance, accounts, 'GET', dataUrl);
          if (dataResponse.data.success) {
            const transformed = config.transformData(dataResponse.data.data || dataResponse.data.rows || []);
            dispatch(actions.setRows(transformed));
            dispatch(actions.setOriginalData(transformed));
          }
        } catch (refreshErr) {
          console.warn('Data refresh after duplicate failed:', refreshErr.message);
        }
      } else {
        throw new Error(response.data.message || 'Duplicate failed');
      }
    } catch (error) {
      console.error('Error duplicating row:', error);
      dispatch(actions.showToast(`Failed to duplicate row: ${error.message}`, 'error'));
    }
  }, [instance, accounts, projectId, config]);

  // Note: handleSelectAllRows is defined after processedData (line ~820)

  // ============================================
  // Shop/Field Ratio Handler (Admin only)
  // ============================================

  const handleShopFieldRatioSuccess = useCallback((result) => {
    dispatch(actions.showToast(result.message, 'success'));
    // Reload to reflect the recalculated earned hours
    window.location.reload();
  }, []);

  // ============================================
  // Filter & Sort Handlers
  // ============================================
  
  const handleFilterToggle = useCallback((columnId) => {
    const currentOpen = stateRef.current.ui.openFilterColumn;
    dispatch(actions.setOpenFilterColumn(currentOpen === columnId ? null : columnId));
  }, []);

  const handleFilterToggleValue = useCallback((columnId, value) => {
    const currentFilters = stateRef.current.ui.columnFilters;
    const currentValues = currentFilters[columnId] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    dispatch(actions.setColumnFilter(columnId, newValues));
  }, []);

  const handleFilterClear = useCallback((columnId) => {
    dispatch(actions.clearColumnFilter(columnId));
    dispatch(actions.clearTextFilter(columnId));
  }, []);

  const handleClearAllFilters = useCallback(() => {
    dispatch(actions.clearAllFilters());
    // Also close any open filter popup
    dispatch(actions.setOpenFilterColumn(null));
  }, []);

  const handleFilterSelectAll = useCallback((columnId, values) => {
    dispatch(actions.setColumnFilter(columnId, values));
  }, []);

  const handleTextFilter = useCallback((columnId, filter) => {
    if (filter.text || filter.mode === 'is_empty' || filter.mode === 'is_not_empty') {
      dispatch(actions.setTextFilter(columnId, filter));
    } else {
      dispatch(actions.clearTextFilter(columnId));
    }
  }, []);

  const handleSort = useCallback((columnId) => {
    console.log('handleSort clicked', columnId);
    const currentSort = stateRef.current.ui.sortConfig;
    let direction = 'asc';
    if (currentSort.key === columnId) {
      if (currentSort.direction === 'asc') direction = 'desc';
      else direction = null; // Toggle off
    }
    
    if (direction === null) {
      dispatch(actions.setSortConfig(null, 'asc'));
    } else {
      dispatch(actions.setSortConfig(columnId, direction));
    }
  }, []);

  const handleToggleColumnGroup = useCallback((groupName) => {
    dispatch({ type: 'TOGGLE_COLUMN_GROUP', payload: groupName });
  }, []);

  // ============================================
  // Computed Values (Filtering, Sorting, Search)
  // ============================================
  
  const getUniqueColumnValues = useCallback((columnId) => {
    const rows = state.data.rows;
    const values = new Set();
    let hasBlank = false;
    rows.forEach(row => {
      const val = row[columnId];
      if (val === null || val === undefined || val === '' || String(val) === '') { hasBlank = true; }
      else { values.add(String(val)); }
    });
    const sorted = [...values].sort();
    if (hasBlank) sorted.push('(Blank)');
    return sorted;
  }, [state.data.rows]);

  // Row-level sub-discipline filter (Piping: udfType, Electrical: divisionArea)
  const [subDisciplineFilter, setSubDisciplineFilter] = useState('all');
  const subDisciplineConfig = useMemo(() => {
    if (disciplineId === 'piping' || disciplineId === 'piping-insulation') {
      return { field: 'udfType', label: 'Sub-discipline', options: PIPING_SUB_DISCIPLINES };
    }
    if (disciplineId === 'electrical') {
      return { field: 'divisionArea', label: 'Sub-discipline', options: ELECTRICAL_SUB_DISCIPLINES };
    }
    return null;
  }, [disciplineId]);

  useEffect(() => {
    setSubDisciplineFilter('all');
  }, [disciplineId, projectId]);

  const processedData = useMemo(() => {
    let processed = [...state.data.rows];

    // 0. Sub-discipline filter (Piping / Tubing / Supports, or Power / I&C / …)
    if (subDisciplineConfig && subDisciplineFilter && subDisciplineFilter !== 'all') {
      const field = subDisciplineConfig.field;
      processed = processed.filter((row) => String(row[field] || '') === subDisciplineFilter);
    }

    // 0b. Tag filter (row matches when it carries ANY of the selected tags)
    if (tagEntityType && tagFilterIds.length > 0) {
      const wanted = new Set(tagFilterIds);
      processed = processed.filter((row) => {
        const rowTags = tagsByEntity[String(row.id)];
        return rowTags && rowTags.some((t) => wanted.has(t.id));
      });
    }
    
    // 1. Column Filters (checkbox selection)
    Object.keys(state.ui.columnFilters).forEach(columnId => {
      const selectedValues = state.ui.columnFilters[columnId];
      if (selectedValues && selectedValues.length > 0) {
        processed = processed.filter(row => {
          const val = row[columnId];
          const isBlank = val === null || val === undefined || val === '' || String(val) === '';
          if (isBlank) return selectedValues.includes('(Blank)');
          return selectedValues.includes(String(val));
        });
      }
    });
    
    // 2. Text/Numeric Filters (search-based)
    Object.keys(state.ui.textFilters || {}).forEach(columnId => {
      const filter = state.ui.textFilters[columnId];
      if (filter && (filter.text || filter.mode === 'is_empty' || filter.mode === 'is_not_empty')) {
        // Check if this is a numeric filter mode
        const isNumericMode = ['greater_than', 'greater_or_equal', 'less_than', 'less_or_equal'].includes(filter.mode);
        
        processed = processed.filter(row => {
          const rawValue = row[columnId];
          const cellValue = String(rawValue || '').toLowerCase();
          const searchText = (filter.text || '').toLowerCase();
          
          // Numeric comparison for numeric modes
          if (isNumericMode || (filter.mode === 'equals' && !isNaN(parseFloat(filter.text)))) {
            const numValue = parseFloat(rawValue);
            const filterNum = parseFloat(filter.text);
            
            if (isNaN(numValue)) return false;
            if (isNaN(filterNum)) return true;
            
            switch (filter.mode) {
              case 'equals':
                return numValue === filterNum;
              case 'not_equals':
                return numValue !== filterNum;
              case 'greater_than':
                return numValue > filterNum;
              case 'greater_or_equal':
                return numValue >= filterNum;
              case 'less_than':
                return numValue < filterNum;
              case 'less_or_equal':
                return numValue <= filterNum;
              default:
                return true;
            }
          }
          
          // Text comparison for text modes
          switch (filter.mode) {
            case 'contains':
              return cellValue.includes(searchText);
            case 'equals':
              return cellValue === searchText;
            case 'not_contains':
              return !cellValue.includes(searchText);
            case 'not_equals':
              return cellValue !== searchText;
            case 'starts_with':
              return cellValue.startsWith(searchText);
            case 'ends_with':
              return cellValue.endsWith(searchText);
            case 'is_empty':
              return cellValue === '';
            case 'is_not_empty':
              return cellValue !== '';
            default:
              return true;
          }
        });
      }
    });
    
    // 3. Global Search
    if (state.ui.searchQuery) {
      const query = state.ui.searchQuery.toLowerCase();
      processed = processed.filter(row => 
        allColumns.some(col => {
          const val = row[col.accessor];
          return val && String(val).toLowerCase().includes(query);
        })
      );
    }
    
    // 3. Sorting
    if (state.ui.sortConfig.key) {
      const { key, direction } = state.ui.sortConfig;
      const column = allColumns.find(c => c.id === key);
      const isNumeric = column?.type === 'number' || column?.type === 'progress' || column?.type === 'progress-readonly';
      
      /**
       * Parse pipe size strings including fractions
       * Handles: "1/2", "1-1/2", "18", "1.75", "3/4"", etc.
       * Returns numeric value or null if not parseable
       */
      const parsePipeSize = (val) => {
        if (val === null || val === undefined) return null;
        const str = String(val).replace(/"/g, '').replace(/'/g, '').trim();
        if (!str) return null;
        
        // Handle mixed fractions like "1-1/2" → 1.5
        const mixedMatch = str.match(/^(\d+)-(\d+)\/(\d+)$/);
        if (mixedMatch) {
          const whole = parseInt(mixedMatch[1], 10);
          const num = parseInt(mixedMatch[2], 10);
          const denom = parseInt(mixedMatch[3], 10);
          if (denom !== 0) return whole + (num / denom);
        }
        
        // Handle simple fractions like "1/2" → 0.5
        const fractionMatch = str.match(/^(\d+)\/(\d+)$/);
        if (fractionMatch) {
          const num = parseInt(fractionMatch[1], 10);
          const denom = parseInt(fractionMatch[2], 10);
          if (denom !== 0) return num / denom;
        }
        
        // Handle whole numbers or decimals
        const num = parseFloat(str);
        return isNaN(num) ? null : num;
      };
      
      // Pre-compute parsed values for non-numeric columns to avoid repeated parsing during sort
      // This reduces parse calls from O(n log n) comparisons to O(n) - significant for large datasets
      let sortCache = null;
      if (!isNumeric) {
        sortCache = new Map();
        for (const row of processed) {
          sortCache.set(row.id, parsePipeSize(row[key]));
        }
      }
      
      processed.sort((a, b) => {
        let valA = a[key];
        let valB = b[key];
        
        if (valA === valB) return 0;
        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;
        
        if (isNumeric) {
          return direction === 'asc' ? valA - valB : valB - valA;
        }
        
        // Use cached parsed values for text columns
        const numA = sortCache.get(a.id);
        const numB = sortCache.get(b.id);
        if (numA !== null && numB !== null) {
          return direction === 'asc' ? numA - numB : numB - numA;
        }
        
        // Fall through to string comparison for non-numeric values
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
        
        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return processed;
  }, [state.data.rows, state.ui.columnFilters, state.ui.textFilters, state.ui.searchQuery, state.ui.sortConfig, allColumns, subDisciplineConfig, subDisciplineFilter, tagEntityType, tagFilterIds, tagsByEntity]);
  processedDataRef.current = processedData;

  // Subtotal bar: credit-weighted % for progress, sum for length, avg for size, unique counts for text
  const subtotalData = useMemo(() => {
    if (!processedData.length || !allColumns.length) return null;
    const result = {};

    const totalCredit = processedData.reduce((sum, r) => sum + (parseFloat(r.credit) || 0), 0);

    allColumns.forEach(col => {
      if (col.type === 'progress' || col.type === 'progress-readonly') {
        if (totalCredit > 0) {
          if (col.accessor === 'totalShopCompletion') {
            const fabRows = processedData.filter((r) => !isFieldRunShop(r.fabricationShop));
            const fabCredit = fabRows.reduce((sum, r) => sum + (parseFloat(r.credit) || 0), 0);
            const totalEarnedShop = fabRows.reduce((sum, r) => sum + (parseFloat(r.earnedCreditShop) || 0), 0);
            result[col.accessor] = fabCredit > 0 ? Math.round((totalEarnedShop / fabCredit) * 1000) / 10 : null;
          } else if (col.accessor === 'totalFieldCompletion') {
            const totalEarnedField = processedData.reduce((sum, r) => sum + (parseFloat(r.earnedCreditField) || 0), 0);
            result[col.accessor] = Math.round((totalEarnedField / totalCredit) * 1000) / 10;
          } else {
            const weightedSum = processedData.reduce((sum, r) => {
              return sum + ((parseFloat(r.credit) || 0) * (parseFloat(r[col.accessor]) || 0));
            }, 0);
            result[col.accessor] = Math.round((weightedSum / totalCredit) * 10) / 10;
          }
        } else {
          const vals = processedData.map(r => parseFloat(r[col.accessor]) || 0);
          result[col.accessor] = vals.length > 0 ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : 0;
        }
      } else if (col.accessor === 'length1') {
        const sum = processedData.reduce((s, r) => s + (parseFloat(r.length1) || 0), 0);
        result[col.accessor] = Math.round(sum * 10) / 10;
      } else if (col.accessor === 'size1') {
        const vals = processedData.map(r => parseFloat(r.size1) || 0).filter(v => v > 0);
        result[col.accessor] = vals.length > 0 ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100 : 0;
      } else if (col.type === 'text' || col.type === 'select' || col.type === 'fabrication-shop' || col.type === 'link' || col.type === 'date' || col.type === 'priority') {
        result[col.accessor] = new Set(processedData.map(r => r[col.accessor]).filter(Boolean)).size;
      }
    });
    return result;
  }, [processedData, allColumns]);

  // ============================================
  // Autofit Column Widths
  // ============================================

  const handleAutofit = useCallback(() => {
    if (!allColumns.length || !processedData.length) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    const PADDING = 24;
    const MIN_WIDTH = 40;
    const MAX_WIDTH = 300;
    const SAMPLE_SIZE = 200;

    const sampled = processedData.length <= SAMPLE_SIZE
      ? processedData
      : processedData.slice(0, SAMPLE_SIZE);

    allColumns.forEach((col) => {
      ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      let maxW = ctx.measureText(col.header || '').width;

      ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      for (const row of sampled) {
        const val = row[col.accessor];
        if (val != null) {
          const text = String(val);
          const w = ctx.measureText(text).width;
          if (w > maxW) maxW = w;
        }
      }

      const fitWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.ceil(maxW + PADDING)));
      setColumnWidth(col.id, fitWidth);
    });
  }, [allColumns, processedData, setColumnWidth]);

  // Compute active filters for display in the filter bar
  const activeFilters = useMemo(() => {
    const filters = [];
    
    // Column value filters
    Object.entries(state.ui.columnFilters || {}).forEach(([columnId, values]) => {
      if (values && values.length > 0) {
        const column = allColumns.find(c => c.id === columnId);
        filters.push({
          columnId,
          columnName: column?.header || columnId,
          type: 'values',
          count: values.length
        });
      }
    });
    
    // Text/Numeric filters
    Object.entries(state.ui.textFilters || {}).forEach(([columnId, filter]) => {
      if (filter && (filter.text || filter.mode === 'is_empty' || filter.mode === 'is_not_empty')) {
        const column = allColumns.find(c => c.id === columnId);
        filters.push({
          columnId,
          columnName: column?.header || columnId,
          type: 'text',
          mode: filter.mode,
          value: filter.text
        });
      }
    });
    
    return filters;
  }, [state.ui.columnFilters, state.ui.textFilters, allColumns]);

  const hasActiveFilters = activeFilters.length > 0 || (state.ui.searchQuery && state.ui.searchQuery.trim() !== '');

  // Report filter state to parent component
  useEffect(() => {
    if (onFilterStateChange) {
      onFilterStateChange({
        hasActiveFilters,
        activeFilters,
        searchQuery: state.ui.searchQuery,
        onClearAll: handleClearAllFilters,
        filteredCount: processedData.length,
        completeCount: state.data.rows.length,
        getRowIds: () => processedDataRef.current.map((row) => row.id),
      });
    }
  }, [hasActiveFilters, activeFilters, state.ui.searchQuery, onFilterStateChange, handleClearAllFilters, processedData.length, state.data.rows.length]);

  // Select all rows handler (needs processedData, so defined here)
  const handleSelectAllRows = useCallback(() => {
    const allIds = processedData.map(row => row.id);
    if (state.ui.selectedRows.size === allIds.length) {
      dispatch(actions.clearSelection());
    } else {
      dispatch(actions.selectAllRows(allIds));
    }
  }, [processedData, state.ui.selectedRows.size]);

  // Jump to + highlight a row from the audit log. When a `field` is provided
  // (the column that changed), also scroll horizontally so that exact column is
  // centered in view.
  const handleNavigateToRow = useCallback((rowId, field) => {
    if (rowId == null) return;
    setShowProgressAudit(false);
    const index = processedData.findIndex(r => String(r.id) === String(rowId));
    if (index === -1) {
      dispatch(actions.showToast('That row is not currently visible (it may be filtered out or deleted).', 'warning'));
      return;
    }
    setHighlightedRowId(String(rowId));
    const ROW_H = 32;
    requestAnimationFrame(() => {
      const scrollEl = mainListRef.current?.querySelector('[style*="overflow"]');
      if (scrollEl) {
        const target = Math.max(0, (index * ROW_H) - (scrollEl.clientHeight / 2) + ROW_H);
        scrollEl.scrollTop = target;
        if (frozenColumnRef.current) frozenColumnRef.current.scrollTop = target;

        // Horizontal scroll to the exact changed column (if it lives in the
        // scrollable area; frozen columns are always visible). All cells in a
        // column share the same horizontal position, so any rendered one works.
        if (field) {
          setTimeout(() => {
            const cell = scrollEl.querySelector(`[data-col-id="${CSS.escape(String(field))}"]`);
            if (cell) {
              const cRect = cell.getBoundingClientRect();
              const sRect = scrollEl.getBoundingClientRect();
              const delta = (cRect.left - sRect.left) - (sRect.width / 2) + (cRect.width / 2);
              scrollEl.scrollLeft += delta;
            }
          }, 60);
        }
      }
    });
    // Auto-clear the highlight after a few seconds
    setTimeout(() => setHighlightedRowId(prev => (String(prev) === String(rowId) ? null : prev)), 4000);
  }, [processedData, dispatch]);

  // Scroll sync for frozen column
  useEffect(() => {
    let scrollHandler = null;
    let mainListElement = null;
    
    const setupScrollSync = () => {
      mainListElement = mainListRef.current?.querySelector('[style*="overflow"]');
      if (mainListElement && frozenColumnRef.current) {
        scrollHandler = () => {
          if (frozenColumnRef.current) {
            frozenColumnRef.current.scrollTop = mainListElement.scrollTop;
          }
        };
        mainListElement.addEventListener('scroll', scrollHandler);
      }
    };
    
    // Delay to ensure List is mounted
    const timer = setTimeout(setupScrollSync, 100);
    
    return () => {
      clearTimeout(timer);
      if (mainListElement && scrollHandler) {
        mainListElement.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [processedData.length]);

  // ============================================
  // Drag & Fill Handlers (with auto-scroll)
  // ============================================

  const ROW_HEIGHT = 32; // must match rowHeight used in List
  const AUTO_SCROLL_ZONE = 50; // px from edge to trigger auto-scroll
  const AUTO_SCROLL_SPEED = 6; // px per frame

  const handleDragStart = useCallback((e, rowIndex, columnId, value) => {
    if (e.button !== 0) return; // Left click only
    e.preventDefault();
    
    setDragState({
      active: true,
      startRow: rowIndex,
      currentDragRow: rowIndex,
      columnId,
      startValue: value
    });
  }, []);

  const handleDragEnter = useCallback((rowIndex) => {
    if (dragStateRef.current.active) {
      setDragState(prev => ({ ...prev, currentDragRow: rowIndex }));
    }
  }, []);

  // Auto-scroll + row tracking via mousemove during drag
  useEffect(() => {
    if (!dragState.active) return;

    const getScrollElement = () => mainListRef.current?.querySelector('[style*="overflow"]');

    const handleMouseMove = (e) => {
      const scrollEl = getScrollElement();
      if (!scrollEl) return;
      const rect = scrollEl.getBoundingClientRect();
      const mouseY = e.clientY;

      // Cancel any pending auto-scroll
      if (dragAutoScrollRef.current) cancelAnimationFrame(dragAutoScrollRef.current);

      // Calculate which row the mouse is over based on scroll position + mouse offset
      const relativeY = mouseY - rect.top + scrollEl.scrollTop;
      const rowIndex = Math.max(0, Math.min(Math.floor(relativeY / ROW_HEIGHT), (processedData.length || 1) - 1));
      setDragState(prev => prev.active ? { ...prev, currentDragRow: rowIndex } : prev);

      // Auto-scroll when mouse is near edges
      const distFromBottom = rect.bottom - mouseY;
      const distFromTop = mouseY - rect.top;

      if (distFromBottom < AUTO_SCROLL_ZONE && distFromBottom > 0) {
        // Near bottom edge → scroll down
        const speed = AUTO_SCROLL_SPEED * (1 - distFromBottom / AUTO_SCROLL_ZONE);
        const scrollDown = () => {
          if (!dragStateRef.current.active) return;
          const el = getScrollElement();
          if (el) {
            el.scrollTop += Math.max(1, speed);
            const newRelY = mouseY - rect.top + el.scrollTop;
            const newRow = Math.max(0, Math.min(Math.floor(newRelY / ROW_HEIGHT), (processedData.length || 1) - 1));
            setDragState(prev => prev.active ? { ...prev, currentDragRow: newRow } : prev);
          }
          dragAutoScrollRef.current = requestAnimationFrame(scrollDown);
        };
        dragAutoScrollRef.current = requestAnimationFrame(scrollDown);
      } else if (distFromTop < AUTO_SCROLL_ZONE && distFromTop > 0) {
        // Near top edge → scroll up
        const speed = AUTO_SCROLL_SPEED * (1 - distFromTop / AUTO_SCROLL_ZONE);
        const scrollUp = () => {
          if (!dragStateRef.current.active) return;
          const el = getScrollElement();
          if (el) {
            el.scrollTop -= Math.max(1, speed);
            const newRelY = mouseY - rect.top + el.scrollTop;
            const newRow = Math.max(0, Math.min(Math.floor(newRelY / ROW_HEIGHT), (processedData.length || 1) - 1));
            setDragState(prev => prev.active ? { ...prev, currentDragRow: newRow } : prev);
          }
          dragAutoScrollRef.current = requestAnimationFrame(scrollUp);
        };
        dragAutoScrollRef.current = requestAnimationFrame(scrollUp);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (dragAutoScrollRef.current) cancelAnimationFrame(dragAutoScrollRef.current);
    };
  }, [dragState.active, processedData.length]);

  const handleDragEnd = useCallback(async () => {
    if (!dragState.active) return;

    // Stop auto-scroll
    if (dragAutoScrollRef.current) cancelAnimationFrame(dragAutoScrollRef.current);

    const { startRow, currentDragRow, columnId, startValue } = dragState;
    const start = Math.min(startRow, currentDragRow);
    const end = Math.max(startRow, currentDragRow);
    
    // Reset drag state immediately
    setDragState({ active: false, startRow: null, currentDragRow: null, columnId: null, startValue: null });

    // Progress columns are locked until RoC is frozen (superadmin bypasses)
    if (!rocFrozen && !state.permissions.isSuperadmin &&
        Array.isArray(progressColumnIdsRef.current) && progressColumnIdsRef.current.includes(columnId)) {
      return;
    }
    
    // Process changes
    const changes = [];
    const batchUpdates = [];
    const affectedRowIds = [];
    
    // We need to operate on processedData because indices match the visible list
    for (let i = start; i <= end; i++) {
      // Skip source row (doesn't change)
      if (i === startRow) continue;
      
      const row = processedData[i];
      if (!row) continue;
      
      // Check edit permissions (editAll covers restricted columns)
      if (!state.permissions.edit && !state.permissions.editAll) continue;
      
      // Build payload for the column change
      const payload = config.buildChangePayload(row.id, columnId, row[columnId], startValue);
      if (payload) {
        changes.push(payload);
        batchUpdates.push({ rowId: row.id, columnId, value: startValue });
        affectedRowIds.push(row.id);
      }
    }
    
    if (changes.length > 0) {
      // Optimistic update - update the dragged column values immediately
      batchUpdates.forEach(update => {
        dispatch(actions.updateRowById(update.rowId, update.columnId, update.value));
        dispatch(actions.addChangedRow(update.rowId));
      });
      
      // API call - backend will recalculate totalFieldCompletion and return it
      // handleBatchSave will update the UI with the backend response
      await handleBatchSave(changes, affectedRowIds);
    }
  }, [dragState, processedData, state.permissions.edit, state.permissions.isSuperadmin, rocFrozen, config, handleBatchSave]);

  // Global mouseup listener for drag end
  useEffect(() => {
    if (dragState.active) {
      window.addEventListener('mouseup', handleDragEnd);
      return () => window.removeEventListener('mouseup', handleDragEnd);
    }
  }, [dragState.active, handleDragEnd]);

  // ============================================
  // Horizontal Drag-Fill (across progress-step columns within one row)
  // ============================================

  // Ordered list of progress-step column ids in the scroll area (the fill targets)
  const progressColumnIds = useMemo(
    () => (scrollColumns || [])
      .filter(c => c.type === 'progress' && c.editable !== false)
      .map(c => c.id),
    [scrollColumns]
  );

  // Mirror progressColumnIds into a ref so the vertical drag-end handler (defined
  // earlier) can read the latest list without creating a temporal-dead-zone dep.
  const progressColumnIdsRef = useRef([]);
  useEffect(() => { progressColumnIdsRef.current = progressColumnIds; }, [progressColumnIds]);

  // Unified fill-start: a single handle that decides direction from the first drag motion.
  // Dragging mostly sideways → horizontal fill across progress columns.
  // Dragging mostly up/down → vertical fill-down across rows (existing behavior).
  const handleFillStart = useCallback((e, rowIndex, rowId, columnId, value) => {
    if (e.button !== 0) return; // Left click only
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const THRESHOLD = 4; // px of movement before committing to an axis

    const cleanup = () => {
      window.removeEventListener('mousemove', onDecideMove);
      window.removeEventListener('mouseup', onDecideUp);
    };
    const onDecideMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
      cleanup();
      if (Math.abs(dx) > Math.abs(dy)) {
        setHDrag({ active: true, rowId, startColId: columnId, currentColId: columnId, startValue: value });
      } else {
        setDragState({ active: true, startRow: rowIndex, currentDragRow: rowIndex, columnId, startValue: value });
      }
    };
    const onDecideUp = () => cleanup(); // released without dragging → no fill

    window.addEventListener('mousemove', onDecideMove);
    window.addEventListener('mouseup', onDecideUp);
  }, []);

  // Track which progress column the cursor is over during horizontal drag
  useEffect(() => {
    if (!hDrag.active) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const cellEl = el.closest('[data-col-id]');
      if (!cellEl) return;
      const colId = cellEl.getAttribute('data-col-id');
      if (colId && progressColumnIds.includes(colId)) {
        setHDrag(prev => (prev.active && prev.currentColId !== colId ? { ...prev, currentColId: colId } : prev));
      }
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [hDrag.active, progressColumnIds]);

  const handleHDragEnd = useCallback(async () => {
    const drag = hDragRef.current;
    if (!drag.active) return;

    const { rowId, startColId, currentColId, startValue } = drag;
    setHDrag({ active: false, rowId: null, startColId: null, currentColId: null, startValue: null });

    if (!state.permissions.edit && !state.permissions.editAll) return;
    // Progress entry locked until RoC frozen (superadmin bypasses)
    if (!rocFrozen && !state.permissions.isSuperadmin) return;

    const sIdx = progressColumnIds.indexOf(startColId);
    const cIdx = progressColumnIds.indexOf(currentColId);
    if (sIdx === -1 || cIdx === -1 || sIdx === cIdx) return;

    const row = processedData.find(r => r.id === rowId);
    if (!row) return;

    const lo = Math.min(sIdx, cIdx);
    const hi = Math.max(sIdx, cIdx);
    const changes = [];
    const batchUpdates = [];

    for (let i = lo; i <= hi; i++) {
      const colId = progressColumnIds[i];
      if (colId === startColId) continue; // source cell doesn't change
      const payload = config.buildChangePayload(rowId, colId, row[colId], startValue);
      if (payload) {
        changes.push(payload);
        batchUpdates.push({ colId, value: startValue });
      }
    }

    if (changes.length > 0) {
      // Optimistic update across the filled columns
      batchUpdates.forEach(u => dispatch(actions.updateRowById(rowId, u.colId, u.value)));
      dispatch(actions.addChangedRow(rowId));
      // Backend recalculates totalFieldCompletion and handleBatchSave applies it
      await handleBatchSave(changes, [rowId]);
    }
  }, [progressColumnIds, processedData, state.permissions.edit, state.permissions.editAll, state.permissions.isSuperadmin, rocFrozen, config, handleBatchSave, dispatch]);

  // Global mouseup listener for horizontal drag end
  useEffect(() => {
    if (hDrag.active) {
      window.addEventListener('mouseup', handleHDragEnd);
      return () => window.removeEventListener('mouseup', handleHDragEnd);
    }
  }, [hDrag.active, handleHDragEnd]);

  // Alarm shown when a user clicks a progress cell that is locked because this
  // discipline's Rules of Credit have not been frozen yet.
  const handleProgressLockedClick = useCallback(() => {
    const msg = rocCanFreeze
      ? 'Progress is locked. Open the “Rules of Credit” button, review the percentages, and Freeze them to start entering progress.'
      : 'Progress is locked until a PM or Technical Coordinator freezes this discipline’s Rules of Credit. Please ask them to freeze it first.';
    dispatch(actions.showToast(msg, 'warning'));
  }, [rocCanFreeze, dispatch]);

  // ============================================
  // Renderers
  // ============================================

  const renderTableCell = useCallback((column, value, rowData, rowIndex, hasEditPermission) => {
    const isRetrying = retryingRows.has(rowData.id);
    const hasEditAllPermission = state.permissions.editAll;
    const canEditRestricted = column.restrictedEdit && hasEditAllPermission;
    
    switch (column.type) {
      case 'link':
        return (
          <div
            onClick={() => onLineTagClick && onLineTagClick(rowData.id, value)}
            style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
            title="Click to view progress trends"
          >
            {value}
          </div>
        );
      case 'progress': {
        if (column.accessor === 'totalShopCompletion' && isFieldRunShop(rowData.fabricationShop)) {
          return (
            <div
              title="Field Run has no shop fabrication. Use Site if this pipe is fabricated on site."
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#94a3b8', fontSize: '12px', fontWeight: '600', fontStyle: 'italic',
              }}
            >
              N/A
            </div>
          );
        }
        // Progress entry is locked until this discipline's Rules of Credit are frozen
        // (superadmin bypasses for corrections).
        const progressAllowed = rocFrozen || state.permissions.isSuperadmin;
        // The user can normally edit, but progress is blocked because the RoC is not frozen.
        const lockedByRoc = hasEditPermission && !progressAllowed;
        return (
          <ProgressCell
            value={value} rowIndex={rowIndex} rowId={rowData.id} columnId={column.id}
            options={column.options || [0, 25, 50, 75, 100]}
            hasEditPermission={hasEditPermission && progressAllowed}
            lockedByRoc={lockedByRoc}
            onLockedClick={handleProgressLockedClick}
            onUpdate={handleCellUpdate} onSave={handleCellSave}
            showDragHandle={config.features.dragFill}
            onFillStart={config.features.dragFill ? handleFillStart : undefined}
            isDragging={dragState.active || hDrag.active}
            isRetrying={isRetrying}
          />
        );
      }
      case 'progress-readonly':
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold', ...getProgressStyle(value) }}>
              {value}%
            </span>
          </div>
        );
      case 'date':
        return (
          <DateCell
            value={value} rowIndex={rowIndex} rowId={rowData.id} columnId={column.id}
            hasEditPermission={hasEditPermission}
            onUpdate={handleCellUpdate} onSave={handleCellSave}
            showDragHandle={config.features.dragFill}
            onDragFillStart={handleDragStart}
            isDragging={dragState.active}
            isRetrying={isRetrying}
            showTimestamp={column.showTimestamp || false}
          />
        );
      case 'priority':
        return (
          <PriorityCell
            value={rowData.priorityName} priorityId={rowData.priority}
            rowIndex={rowIndex} rowId={rowData.id} columnId={column.id}
            priorities={state.data.priorities}
            hasEditPermission={hasEditPermission}
            onUpdate={handleCellUpdate} onSave={handleCellSave}
            isRetrying={isRetrying}
          />
        );
      case 'fabrication-shop':
        return (
          <FabricationShopCell
            value={value}
            rowIndex={rowIndex}
            rowId={rowData.id}
            columnId={column.id}
            locations={state.data.fabricationLocations}
            hasEditPermission={column.editable && hasEditPermission}
            onUpdate={handleCellUpdate}
            onSave={handleCellSave}
            showDragHandle={config.features.dragFill}
            onDragFillStart={handleDragStart}
            isDragging={dragState.active}
            isRetrying={isRetrying}
            onLocationAdded={handleLocationAdded}
          />
        );
      case 'hold-checkbox':
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <input
              type="checkbox"
              checked={!!value}
              disabled={!hasEditPermission}
              onChange={(e) => {
                const newVal = e.target.checked;
                handleCellUpdate(rowData.id, column.id, newVal);
                handleCellSave(rowData.id, column.id, value, newVal);
              }}
              style={{
                cursor: hasEditPermission ? 'pointer' : 'default',
                width: '16px',
                height: '16px',
                accentColor: value ? '#dc3545' : undefined,
              }}
              title={value ? `On hold${rowData.holdUpdatedBy ? ` by ${rowData.holdUpdatedBy}` : ''}` : 'Click to put on hold'}
            />
          </div>
        );
      case 'select':
        if ((column.editable && hasEditPermission) || canEditRestricted) {
          const selectOptions = dynamicOptions[column.id] || column.options || [];
          return (
            <SelectCell
              value={value || ''}
              rowId={rowData.id}
              columnId={column.id}
              options={selectOptions}
              hasEditPermission={true}
              onUpdate={handleCellUpdate}
              onSave={handleCellSave}
              isDragging={dragState.active}
              onDragFillStart={config.features.dragFill ? handleDragStart : undefined}
              rowIndex={rowIndex}
              showDragHandle={!!config.features.dragFill}
              isRetrying={isRetrying}
              allowAddNew={!!column.allowAddNew}
              onOptionAdded={column.allowAddNew ? handleSelectOptionAdded : undefined}
            />
          );
        }
        return value || '';
      case 'tags': {
        const rowTags = tagsByEntity[String(rowData.id)] || [];
        return (
          <TagCell
            entityType={tagEntityType}
            entityId={rowData.id}
            projectId={numericProjectId}
            tags={rowTags}
            onAssign={(tag) => assignEntityTag(tag, rowData.id)}
            onUnassign={(tag) => unassignEntityTag(tag, rowData.id)}
            canEdit={true}
          />
        );
      }
      case 'text':
      case 'number':
        if ((column.editable && hasEditPermission) || canEditRestricted) {
          return (
            <EditableCell
              value={value} rowIndex={rowIndex} rowId={rowData.id} columnId={column.id}
              hasEditPermission={true}
              onUpdate={handleCellUpdate} onSave={handleCellSave}
              showDragHandle={config.features.dragFill}
              onDragFillStart={handleDragStart}
              isDragging={dragState.active}
              isRetrying={isRetrying}
            />
          );
        }
        return value || '';
      default:
        return value || '';
    }
  }, [config, state.permissions.edit, state.permissions.editAll, state.permissions.isSuperadmin, rocFrozen, handleProgressLockedClick, state.data.priorities, state.data.fabricationLocations, handleCellUpdate, handleCellSave, handleDragStart, handleFillStart, dragState.active, hDrag.active, onLineTagClick, retryingRows, handleLocationAdded, dynamicOptions, handleSelectOptionAdded, tagsByEntity, tagEntityType, numericProjectId, assignEntityTag, unassignEntityTag]);

  // Memoize row props for List (react-window v2.x API)
  // IMPORTANT: Avoid passing entire `state` object - extract only needed values
  // This prevents React Fast Refresh infinite recursion issues
  const changedRows = state.changedRows;
  const collapsedGroups = state.ui.collapsedGroups;
  const selectedRows = state.ui.selectedRows;
  const hasEditPermission = state.permissions.edit;
  const hasDeletePermission = state.permissions.delete;
  
  const rowProps = useMemo(() => ({
    rows: processedData,
    columns: scrollColumns,
    config,
    changedRows,
    renderTableCell,
    onDragEnter: handleDragEnter,
    dragState,
    hDrag,
    progressColumnIds,
    collapsedGroups,
    selectedRows,
    onToggleRowSelection: handleToggleRowSelection,
    hasEditPermission,
    hasDeletePermission,
    columnWidths,
    disciplineId,
    highlightedRowId,
    // When action columns are pinned, they render in the frozen pane instead of the row
    renderActionColumns: !freezeActionColumns,
    onDuplicateRow: hasDuplicateFeature ? handleDuplicateSingleRow : undefined,
  }), [processedData, scrollColumns, config, changedRows, renderTableCell, handleDragEnter, dragState, hDrag, progressColumnIds, collapsedGroups, selectedRows, handleToggleRowSelection, hasEditPermission, hasDeletePermission, columnWidths, disciplineId, highlightedRowId, freezeActionColumns, handleDuplicateSingleRow]);

  // ============================================
  // Main Render
  // ============================================
  
  // Loading state
  if (state.loading.data && !state.ui.hasLoadedOnce) {
    return (
      <div style={{ textAlign: 'center', padding: '5rem', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #f3f3f3', borderTop: '4px solid #007bff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <div style={{ color: '#666', fontSize: '16px', fontWeight: '500' }}>Loading {config.name} data...</div>
        </div>
      </div>
    );
  }

  const matrixButtonStyle = {
    padding: '0.5rem 1rem',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease'
  };

  return (
    <div>
      {/* Progress-locked banner: shown until this discipline's Rules of Credit are frozen */}
      {rocChecked && !rocFrozen && !state.permissions.isSuperadmin && (
        <div style={{
          marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '6px',
          backgroundColor: '#fff3cd', border: '1px solid #ffc107', color: '#856404',
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '1.1rem' }}>🔒</span>
          <span style={{ flex: 1, fontSize: '0.9rem' }}>
            <strong>Progress entry is locked.</strong> Rules of Credit must be reviewed and frozen
            {rocCanFreeze ? ' (use the “Rules of Credit” button)' : ' by a PM or Technical Coordinator'} before progress can be entered for this discipline.
          </span>
          {rocCanFreeze && state.permissions.roc && (
            <button
              onClick={onRulesOfCreditClick}
              style={{ padding: '0.4rem 0.9rem', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
            >
              Open Rules of Credit
            </button>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', minWidth: 0 }}>
          {/* Search Bar */}
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 0, maxWidth: 250 }}>
            <input
              type="text"
              placeholder="Search all columns..."
              value={state.ui.searchQuery}
              onChange={(e) => dispatch(actions.setSearchQuery(e.target.value))}
              style={{
                padding: '6px 12px',
                paddingLeft: '30px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '14px',
                // Fills its flex track instead of pinning 250px, so the toolbar can wrap
                // on a phone rather than pushing the buttons past the screen edge.
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}>🔍</span>
          </div>
          
          <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
            Showing {processedData.length} of {state.data.rows.length} rows
          </span>
          
          {state.hasUnsavedChanges && (
            <span style={{ marginLeft: '1rem', color: '#856404', backgroundColor: '#fff3cd', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
              {state.changedRows.size} unsaved changes
            </span>
          )}

          {/* Show-deleted scope (superadmin only; not for NDE hard-delete tracker) */}
          {state.permissions.isSuperadmin && !isHardDeleteTracker && (
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: deletedView === 'active' ? '#666' : '#b91c1c' }}
              title="Show soft-deleted rows (superadmin)"
            >
              🗂️ View:
              <select
                value={deletedView}
                onChange={(e) => { setDeletedView(e.target.value); dispatch(actions.clearSelection()); }}
                style={{ padding: '3px 6px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '13px' }}
              >
                <option value="active">Active</option>
                <option value="deleted">Deleted only</option>
                <option value="all">All (incl. deleted)</option>
              </select>
            </label>
          )}
          
          {/* Mechanical Sub-Table Dropdown */}
          {(disciplineId === 'mechanical' || disciplineId === 'mechanical-rotating-eqp' || 
            disciplineId === 'vessels' || disciplineId === 'mechanical-vessels' ||
            disciplineId === 'chutes' || disciplineId === 'mechanical-chutes' ||
            disciplineId === 'conveyors' || disciplineId === 'mechanical-conveyors' ||
            disciplineId === 'skids' || disciplineId === 'mechanical-skids') && onMechanicalSubTableChange && (
            <MechanicalTableDropdown
              currentSubTableId={mechanicalSubTableId || 'rotating-equipment'}
              onSubTableChange={onMechanicalSubTableChange}
              disabled={state.loading.data || state.hasUnsavedChanges}
            />
          )}

          {/* Piping / Electrical row-level sub-discipline filter */}
          {subDisciplineConfig && (
            <SubDisciplineFilter
              label={subDisciplineConfig.label}
              options={subDisciplineConfig.options.map((o) => ({
                ...o,
                count: state.data.rows.filter((r) => String(r[subDisciplineConfig.field] || '') === o.value).length,
              }))}
              value={subDisciplineFilter}
              onChange={setSubDisciplineFilter}
              disabled={state.loading.data}
            />
          )}
          
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Piping Sub-View Dropdown - on the right with action buttons */}
          {(disciplineId === 'piping' || disciplineId === 'piping-nde' || disciplineId === 'piping-testing' || disciplineId === 'piping-welder-log') && onPipingSubViewChange && (
            <PipingTrackerDropdown
              currentSubViewId={pipingSubViewId || 'progress-tracker'}
              onSubViewChange={onPipingSubViewChange}
              disabled={state.loading.data || state.hasUnsavedChanges}
            />
          )}

          {/* Active Viewers - Real-time presence indicator */}
          {presenceEnabled && (
            <ActiveViewers
              viewers={presenceViewers}
              isConnected={presenceConnected}
              connectionState={presenceConnectionState}
              maxVisible={4}
              showConnectionStatus={true}
            />
          )}
          
          {/* Help Button */}
          <button
            onClick={() => setShowHelp(true)}
            style={matrixButtonStyle}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#0369a1'; e.target.style.color = 'white'; e.target.style.borderColor = '#0369a1'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
          >
            ❓ Help
          </button>

          {/* Analytics Button - requires pt_analytics permission */}
          {state.permissions.analytics && (
            <button
              onClick={() => setShowAnalytics(true)}
              style={matrixButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              📊 Analytics
            </button>
          )}
          
          {/* Audit Log Button - NDE / Pipe Testing trackers (their own dedicated audit log) */}
          {hasOwnAuditLog && onAuditLogClick && (
            <button
              onClick={onAuditLogClick}
              style={matrixButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#6366f1'; e.target.style.color = 'white'; e.target.style.borderColor = '#6366f1'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              📋 Audit Log
            </button>
          )}

          {/* Audit Log Button - all progress tracker disciplines (excludes NDE / Pipe Testing which have their own) */}
          {!hasOwnAuditLog && (
            <button
              onClick={() => setShowProgressAudit(true)}
              style={matrixButtonStyle}
              title="View change & error history for this tracker"
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#6366f1'; e.target.style.color = 'white'; e.target.style.borderColor = '#6366f1'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              📋 Audit Log
            </button>
          )}

          {/* Duplicate Button - NDE / Pipe Testing trackers, shown when rows selected */}
          {hasDuplicateFeature && state.permissions.edit && state.ui.selectedRows.size > 0 && (
            <button
              onClick={handleDuplicateRows}
              style={{
                padding: '8px 16px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
              title="Duplicate selected rows (copies auto-populated / spec fields only)"
            >
              📄 Duplicate ({state.ui.selectedRows.size})
            </button>
          )}
          
          {/* Bulk Tag Button - shown when rows selected and this tracker supports tags */}
          {tagEntityType && state.ui.selectedRows.size > 0 && deletedView !== 'deleted' && (
            <button
              onClick={(e) => setBulkTagAnchor(e.currentTarget.getBoundingClientRect())}
              style={{
                padding: '8px 16px',
                backgroundColor: '#0e7490',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
              title="Apply or remove tags on all selected rows"
            >
              🏷️ Tag ({state.ui.selectedRows.size})
            </button>
          )}

          {/* Filter-by-tag button */}
          {tagEntityType && (
            <button
              onClick={(e) => setTagFilterAnchor(e.currentTarget.getBoundingClientRect())}
              style={{
                ...matrixButtonStyle,
                ...(tagFilterIds.length > 0 ? { backgroundColor: '#0e7490', color: 'white', borderColor: '#0e7490' } : {}),
              }}
              title="Filter rows by tag (rows matching any selected tag)"
            >
              🏷️ Tag Filter{tagFilterIds.length > 0 ? ` (${tagFilterIds.length})` : ''}
            </button>
          )}
          {tagEntityType && tagFilterIds.length > 0 && (
            <button
              onClick={() => setTagFilterIds([])}
              style={{ ...matrixButtonStyle, padding: '0.5rem 0.6rem' }}
              title="Clear tag filter"
            >
              ✕
            </button>
          )}

          {/* Delete Button - shown when rows selected, requires pt_delete permission.
              Hidden in the "Deleted only" view (those rows are already deleted). */}
          {state.permissions.delete && state.ui.selectedRows.size > 0 && deletedView !== 'deleted' && (
            <button
              onClick={handleBatchDelete}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              🗑️ Delete ({state.ui.selectedRows.size})
            </button>
          )}

          {/* Restore / Purge - superadmin only, when viewing deleted rows */}
          {state.permissions.isSuperadmin && !isHardDeleteTracker && deletedView !== 'active' && state.ui.selectedRows.size > 0 && (
            <>
              <button
                onClick={handleRestoreSelected}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
                title="Restore selected deleted rows back into the tracker"
              >
                ♻️ Restore ({state.ui.selectedRows.size})
              </button>
              <button
                onClick={handlePurgeSelected}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#7f1d1d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
                title="Permanently delete selected rows (cannot be undone)"
              >
                ⚠️ Purge ({state.ui.selectedRows.size})
              </button>
            </>
          )}
          {state.permissions.import && (
            <button
              onClick={onUploadClick}
              style={matrixButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              📤 Import
            </button>
          )}
          {state.permissions.export && (
            <button
              onClick={onExportClick}
              style={matrixButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              📥 Export
            </button>
          )}
          {state.permissions.export && !['piping-nde', 'piping-testing'].includes(disciplineId) && (
            <button
              onClick={() => setShowBarcodePrint(true)}
              style={matrixButtonStyle}
              title="Print Code128 barcodes for ISO / equipment tags (selected rows, or all visible)"
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              ▮▮ Barcodes
            </button>
          )}
          <button
            onClick={handleAutofit}
            style={matrixButtonStyle}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            title="Auto-fit all column widths to content"
          >
            ↔ Autofit
          </button>
          {state.permissions.columnConfig && (
            <button
              onClick={onColumnConfigClick}
              style={matrixButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              ⚙️ Columns
            </button>
          )}
          {state.permissions.roc && (
            <button
              onClick={onRulesOfCreditClick}
              style={matrixButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              📋 Rules of Credit
            </button>
          )}
          {/* Shop/Field Ratio - Piping Only, requires pt_sfratio permission */}
          {state.permissions.sfRatio && disciplineId === 'piping' && (
            <button
              onClick={() => setShowShopFieldRatioModal(true)}
              style={matrixButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              ⚙️ S/F Ratio
            </button>
          )}
          {/* Mat Bore % - Piping Only, requires pt_matbore permission */}
          {state.permissions.matBore && disciplineId === 'piping' && onMatBoreClick && (
            <button
              onClick={onMatBoreClick}
              style={matrixButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = 'white'; e.target.style.borderColor = '#dc2626'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#374151'; e.target.style.borderColor = '#e5e7eb'; }}
            >
              📊 Mat Bore %
            </button>
          )}
        </div>
      </div>

      {/* Shop/Field Ratio Modal */}
      <ShopFieldRatioModal
        isOpen={showShopFieldRatioModal}
        onClose={() => setShowShopFieldRatioModal(false)}
        jobNumber={projectDetails?.job_number}
        onSuccess={handleShopFieldRatioSuccess}
      />

      <BarcodePrintModal
        open={showBarcodePrint}
        onClose={() => setShowBarcodePrint(false)}
        disciplineId={disciplineId}
        projectId={projectId}
        projectDetails={projectDetails}
        rows={processedData}
        selectedRowIds={state.ui.selectedRows}
      />

      {/* Virtualized Table */}
      {(() => {
        const tableHeight = Math.min(Math.max(500, window.innerHeight - 350), 800);
        const headerHeight = 80; // Increased to show full filter button
        const rowHeight = 32;

        // ---- Frozen pane geometry ----
        // Width of each pinned data column (respects resize), in pinned order
        const frozenColumnWidthList = frozenColumns.map(c => getColumnWidth(c.id, c.width || 140));
        const frozenDataWidth = frozenColumnWidthList.reduce((s, w) => s + w, 0);

        // Action columns (+, checkbox)
        const deleteColWidth = state.permissions.delete ? 32 : 0;
        const duplicateColWidth = (hasDuplicateFeature && state.permissions.edit) ? 28 : 0;
        const actionColumnsWidth = deleteColWidth + duplicateColWidth;

        // Total width of the pinned/frozen left region
        const frozenPaneWidth = frozenDataWidth + (freezeActionColumns ? actionColumnsWidth : 0);

        // Pinned slots (left-to-right) used to position sticky header cells.
        // When action columns are frozen they sit left of the pinned data columns.
        const frozenSlots = [];
        if (freezeActionColumns) {
          if (duplicateColWidth) frozenSlots.push({ kind: 'duplicate', width: duplicateColWidth });
          if (deleteColWidth) frozenSlots.push({ kind: 'checkbox', width: deleteColWidth });
        }
        frozenColumns.forEach((col, i) => {
          frozenSlots.push({ kind: 'data', column: col, width: frozenColumnWidthList[i], isFirstData: i === 0 });
        });
        // Precompute cumulative left offset for each slot
        let _leftAcc = 0;
        frozenSlots.forEach(slot => { slot.left = _leftAcc; _leftAcc += slot.width; });

        // Get unique groups for collapse buttons (scrollable columns only)
        const uniqueGroups = [...new Set(scrollColumns.map(col => col.headerGroup).filter(Boolean))];
        const groupButtonWidth = 18;

        // Visible scrollable columns (exclude collapsed groups)
        const visibleScrollColumns = scrollColumns.filter(col => {
          if (!col.headerGroup) return true; // Show ungrouped columns
          return !state.ui.collapsedGroups?.[col.headerGroup]; // Show if group not collapsed
        });

        const scrollbarPadding = 16;
        // Width of the scrollable area = visible scroll columns + group buttons + scrollbar
        //   + action columns only when they are NOT frozen (otherwise they're in the frozen pane)
        const scrollAreaWidth = visibleScrollColumns.reduce((sum, col) => sum + (columnWidths[col.id] || col.width), 0)
          + (uniqueGroups.length * groupButtonWidth)
          + (freezeActionColumns ? 0 : actionColumnsWidth)
          + scrollbarPadding;
        const totalColumnWidth = frozenPaneWidth + scrollAreaWidth;
        
        // Use consistent header height that includes the border
        const headerTotalHeight = headerHeight + 1; // headerHeight + 1px border
        
        return (
          <div ref={tableContainerRef} className="virtualized-table-container" style={{ border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: '#fff', position: 'relative', overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
            {/* Full width inner container for horizontal scroll */}
            <div style={{ width: 'fit-content' }}>
              {/* Header Row */}
              <div style={{ display: 'flex', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', position: 'sticky', top: 0, zIndex: 2, width: 'fit-content' }}>
                {/* Frozen (pinned) Header Cells */}
                {frozenSlots.map((slot) => {
                  const stickyBase = {
                    position: 'sticky',
                    left: slot.left,
                    zIndex: 11,
                    width: slot.width,
                    minWidth: slot.width,
                    maxWidth: slot.width,
                    height: headerHeight,
                    boxSizing: 'border-box',
                    borderRight: '1px solid #dee2e6',
                    backgroundColor: '#f8f9fa',
                  };
                  if (slot.kind === 'duplicate') {
                    return (
                      <div key="header-frozen-duplicate" style={{
                        ...stickyBase, padding: '2px', fontWeight: 'bold', textAlign: 'center',
                        color: '#059669', fontSize: '13px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center',
                      }} title="Duplicate row">+</div>
                    );
                  }
                  if (slot.kind === 'checkbox') {
                    return (
                      <div key="header-frozen-checkbox" style={{
                        ...stickyBase, padding: '4px', fontWeight: 'bold', textAlign: 'center',
                        color: '#495057', fontSize: '11px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center',
                      }} title="Select all visible rows for delete">
                        <input type="checkbox"
                          checked={state.ui.selectedRows.size > 0 && state.ui.selectedRows.size === processedData.length}
                          onChange={handleSelectAllRows} style={{ cursor: 'pointer' }}
                        />
                      </div>
                    );
                  }
                  // Pinned data column header
                  const column = slot.column;
                  const isSorted = state.ui.sortConfig.key === column.id;
                  return (
                    <div
                      key={column.id}
                      data-col-id={column.id}
                      style={{
                        ...stickyBase,
                        padding: '4px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        color: '#495057',
                        fontSize: '11px',
                        backgroundColor: isSorted ? '#e9ecef' : '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        overflow: 'hidden',
                      }}
                      onClick={() => handleSort(column.id)}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', overflow: 'hidden' }}>
                        <div style={{ textAlign: 'center', lineHeight: '1.2', width: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'inline' }}>{column.header}</span>
                          {isSorted && (
                            <span style={{ marginLeft: '2px' }}>{state.ui.sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                          )}
                        </div>
                        {column.filterable && (
                          <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
                            <ColumnFilter
                              columnId={column.id}
                              columnName={column.header}
                              columnType={column.type}
                              isOpen={state.ui.openFilterColumn === column.id}
                              onToggle={handleFilterToggle}
                              selectedValues={state.ui.columnFilters[column.id] || []}
                              uniqueValues={getUniqueColumnValues(column.id)}
                              onToggleValue={handleFilterToggleValue}
                              onClear={handleFilterClear}
                              onSelectAll={handleFilterSelectAll}
                              onTextFilter={handleTextFilter}
                              textFilter={state.ui.textFilters?.[column.id]}
                            />
                          </div>
                        )}
                      </div>
                      {/* Resize handle for pinned column */}
                      <div
                        onMouseDown={(e) => handleResizeStart(e, column.id, slot.width)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '5px',
                          cursor: 'col-resize',
                          backgroundColor: 'transparent',
                          zIndex: 12,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dee2e6'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      />
                    </div>
                  );
                })}
                {/* Other Header Cells */}
                {(() => {
                  const headerItems = [];

                  // Action column headers live in the scrollable area unless they are pinned
                  if (!freezeActionColumns) {
                    // Duplicate column header for NDE / Pipe Testing tracker (if edit permission)
                    if (hasDuplicateFeature && state.permissions.edit) {
                      headerItems.push(
                        <div key="header-duplicate" style={{
                          width: 28, minWidth: 28, maxWidth: 28, height: headerHeight, padding: '2px',
                          fontWeight: 'bold', textAlign: 'center', color: '#059669', fontSize: '13px',
                          borderRight: '1px solid #dee2e6', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', boxSizing: 'border-box', backgroundColor: '#f8f9fa',
                        }} title="Duplicate row">+</div>
                      );
                    }

                    // Checkbox header (if delete permission)
                    if (state.permissions.delete) {
                      headerItems.push(
                        <div key="header-select-checkbox" style={{
                          width: 32, minWidth: 32, maxWidth: 32, height: headerHeight, padding: '4px',
                          fontWeight: 'bold', textAlign: 'center', color: '#495057', fontSize: '11px',
                          borderRight: '1px solid #dee2e6', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', boxSizing: 'border-box', backgroundColor: '#f8f9fa',
                        }} title="Select all visible rows for delete">
                          <input type="checkbox"
                            checked={state.ui.selectedRows.size > 0 && state.ui.selectedRows.size === processedData.length}
                            onChange={handleSelectAllRows} style={{ cursor: 'pointer' }}
                          />
                        </div>
                      );
                    }
                  }

                  let lastGroup = null;
                  const columnsToRender = scrollColumns;
                  
                  columnsToRender.forEach((column, idx) => {
                    const isCollapsed = column.headerGroup && state.ui.collapsedGroups?.[column.headerGroup];
                    const isFirstInGroup = column.headerGroup && column.headerGroup !== lastGroup;
                    const groupColor = column.headerColor || '#f8f9fa';
                    
                    // Add collapse button when starting a new group
                    if (isFirstInGroup) {
                      const groupName = column.headerGroup;
                      const groupCollapsed = state.ui.collapsedGroups?.[groupName];
                      headerItems.push(
                        <div
                          key={`group-btn-${groupName}`}
                          onClick={(e) => { e.stopPropagation(); handleToggleColumnGroup(groupName); }}
                          style={{
                            width: 18,
                            minWidth: 18,
                            maxWidth: 18,
                            height: headerHeight,
                            padding: '2px 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: groupColor,
                            borderRight: groupCollapsed ? '1px solid #dee2e6' : 'none',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#666',
                            boxSizing: 'border-box',
                          }}
                          title={groupCollapsed ? `Expand ${groupName}` : `Collapse ${groupName}`}
                        >
                          {groupCollapsed ? '+' : '−'}
                        </div>
                      );
                    }
                    
                    // Skip rendering column if its group is collapsed
                    if (isCollapsed) {
                      lastGroup = column.headerGroup;
                      return;
                    }
                    
                    const colWidth = getColumnWidth(column.id, column.width);
                    
                    headerItems.push(
                      <div
                        key={column.id}
                        data-col-id={column.id}
                        style={{
                          width: colWidth,
                          minWidth: colWidth,
                          maxWidth: colWidth,
                          height: headerHeight,
                          padding: '4px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          color: '#495057',
                          fontSize: '11px',
                          borderRight: '1px solid #dee2e6',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          backgroundColor: state.ui.sortConfig.key === column.id ? (column.headerColor ? `${column.headerColor}dd` : '#e9ecef') : (column.headerColor || '#f8f9fa'),
                          position: 'relative',
                        }}
                        onClick={() => handleSort(column.id)}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', overflow: 'hidden' }}>
                          <div style={{ textAlign: 'center', lineHeight: '1.2', width: '100%', flex: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '2px' }}>
                            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'inline' }}>{column.header}</span>
                            {state.ui.sortConfig.key === column.id && (
                              <span style={{ marginLeft: '2px' }}>{state.ui.sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                            )}
                          </div>
                          {column.filterable && (
                            <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
                              <ColumnFilter
                                columnId={column.id}
                                columnName={column.header}
                                columnType={column.type}
                                isOpen={state.ui.openFilterColumn === column.id}
                                onToggle={handleFilterToggle}
                                selectedValues={state.ui.columnFilters[column.id] || []}
                                uniqueValues={getUniqueColumnValues(column.id)}
                                onToggleValue={handleFilterToggleValue}
                                onClear={handleFilterClear}
                                onSelectAll={handleFilterSelectAll}
                                onTextFilter={handleTextFilter}
                                textFilter={state.ui.textFilters?.[column.id]}
                              />
                            </div>
                          )}
                        </div>
                        {/* Resize handle */}
                        <div
                          onMouseDown={(e) => handleResizeStart(e, column.id, colWidth)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '5px',
                            cursor: 'col-resize',
                            backgroundColor: 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dee2e6'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        />
                      </div>
                    );
                    
                    lastGroup = column.headerGroup;
                  });
                  
                  // Add spacer for scrollbar padding (16px) at the end of header row
                  headerItems.push(
                    <div
                      key="header-scrollbar-spacer"
                      style={{
                        width: 16,
                        minWidth: 16,
                        maxWidth: 16,
                        height: headerHeight,
                        boxSizing: 'border-box',
                        backgroundColor: '#f8f9fa',
                      }}
                    />
                  );
                  
                  return headerItems;
                })()}
              </div>
              
              {/* Body Row - contains frozen first column and scrollable list */}
              <div style={{ display: 'flex', height: tableHeight - headerTotalHeight, width: 'fit-content' }}>
                {/* Frozen Column Body (pinned action + data columns) */}
                <div 
                  ref={frozenColumnRef}
                  className="frozen-column-body"
                  data-col-id={allColumns[0]?.id}
                  style={{ 
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    width: frozenPaneWidth,
                    minWidth: frozenPaneWidth,
                    maxWidth: frozenPaneWidth,
                    height: tableHeight - headerTotalHeight,
                    backgroundColor: '#fff',
                    overflowY: 'hidden',
                    overflowX: 'hidden',
                  }}
                >
                  {processedData.map((row, index) => {
                    const isChanged = state.changedRows.has(row.id);
                    const frozenIsOnHold = row.onHold === true;
                    const isSelected = state.ui.selectedRows.has(row.id);
                    const frozenIsAuditHighlighted = highlightedRowId != null && String(row.id) === String(highlightedRowId);
                    const rowBgColor = frozenIsAuditHighlighted
                      ? '#fde68a'
                      : frozenIsOnHold
                        ? '#f0e6e6'
                        : isChanged ? '#fff3cd' : (index % 2 === 0 ? '#ffffff' : '#f1f3f5');
                    return (
                      <div
                        key={row.id}
                        style={{
                          display: 'flex',
                          alignItems: 'stretch',
                          width: frozenPaneWidth,
                          height: rowHeight,
                          boxSizing: 'border-box',
                          backgroundColor: rowBgColor,
                          borderBottom: '1px solid #dee2e6',
                          boxShadow: frozenIsAuditHighlighted ? 'inset 0 0 0 2px #f59e0b' : undefined,
                        }}
                      >
                        {/* Pinned duplicate (+) action — NDE only */}
                        {freezeActionColumns && duplicateColWidth > 0 && (
                          <div style={{ width: duplicateColWidth, minWidth: duplicateColWidth, maxWidth: duplicateColWidth, height: '100%', borderRight: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDuplicateSingleRow(row.id); }}
                              title="Duplicate this row"
                              style={{ width: 22, height: 22, padding: 0, border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', lineHeight: 1, backgroundColor: '#fff', color: '#059669', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >+</button>
                          </div>
                        )}
                        {/* Pinned selection checkbox */}
                        {freezeActionColumns && deleteColWidth > 0 && (
                          <div style={{ width: deleteColWidth, minWidth: deleteColWidth, maxWidth: deleteColWidth, height: '100%', borderRight: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', backgroundColor: isSelected ? '#e3f2fd' : 'transparent' }}>
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleRowSelection(row.id)} style={{ cursor: 'pointer' }} title="Select for delete" />
                          </div>
                        )}
                        {/* Pinned data columns */}
                        {frozenColumns.map((col, ci) => {
                          const value = row[col.accessor];
                          const isFirstData = ci === 0;
                          const cellWidth = frozenColumnWidthList[ci];
                          return (
                            <div
                              key={col.id}
                              data-col-id={col.id}
                              style={{
                                width: cellWidth,
                                minWidth: cellWidth,
                                maxWidth: cellWidth,
                                height: '100%',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                fontSize: '11px',
                                boxSizing: 'border-box',
                                borderRight: '1px solid #dee2e6',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                position: 'relative',
                              }}
                              title={isFirstData && frozenIsOnHold ? `ON HOLD — ${value || ''}` : (value != null ? String(value) : '')}
                            >
                              {isFirstData && frozenIsOnHold && <span style={{ marginRight: '3px', fontSize: '11px', flexShrink: 0 }} title="On Hold">🚩</span>}
                              {isFirstData && frozenIsOnHold ? (
                                <div
                                  onClick={() => onLineTagClick && onLineTagClick(row.id, value)}
                                  style={{ color: '#dc2626', cursor: 'pointer', textDecoration: 'underline', fontWeight: '600' }}
                                  title="ON HOLD — Click to view progress trends"
                                >
                                  {value}
                                </div>
                              ) : (
                                renderTableCell(col, value, row, index, state.permissions.edit)
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                
                {/* Main Virtualized List */}
                <div 
                  ref={mainListRef}
                  className="main-list-container"
                  style={{ width: scrollAreaWidth, height: tableHeight - headerTotalHeight }}
                >
                  <List
                    rowComponent={Row}
                    rowCount={processedData.length || 0}
                    rowHeight={rowHeight}
                    rowProps={rowProps}
                    overscanCount={30}
                    style={{ height: tableHeight - headerTotalHeight, width: scrollAreaWidth, overflowX: 'hidden', overflowY: 'auto' }}
                  />
                </div>
              </div>
            </div>
            {/* Subtotal Bar - inside table container to scroll together */}
            {subtotalData && processedData.length > 0 && (
              <div style={{ display: 'flex', borderTop: '2px solid #374151', width: 'fit-content' }}>
                {/* Frozen (pinned) region — spans the pinned action + data columns */}
                <div style={{
                  width: frozenPaneWidth, minWidth: frozenPaneWidth, maxWidth: frozenPaneWidth,
                  padding: '6px 8px', fontSize: '11px', fontWeight: '700', color: '#1e293b',
                  backgroundColor: '#f1f5f9', borderRight: '1px solid #dee2e6',
                  display: 'flex', alignItems: 'center', boxSizing: 'border-box',
                  position: 'sticky', left: 0, zIndex: 10,
                }}>
                  {processedData.length === state.data.rows.length ? `${processedData.length} rows` : `${processedData.length} / ${state.data.rows.length}`}
                </div>
                {/* Action column spacers in subtotal (only when actions are NOT pinned) */}
                {!freezeActionColumns && hasDuplicateFeature && state.permissions.edit && (
                  <div style={{ width: 28, minWidth: 28, maxWidth: 28, padding: '6px 0', backgroundColor: '#f8f9fa', borderRight: '1px solid #dee2e6', boxSizing: 'border-box' }} />
                )}
                {!freezeActionColumns && state.permissions.delete && (
                  <div style={{ width: 32, minWidth: 32, maxWidth: 32, padding: '6px 0', backgroundColor: '#f8f9fa', borderRight: '1px solid #dee2e6', boxSizing: 'border-box' }} />
                )}
                {(() => {
                  const subtotalCells = [];
                  let lastGrp = null;
                  scrollColumns.forEach(col => {
                    const isCollapsed = col.headerGroup && state.ui.collapsedGroups?.[col.headerGroup];
                    const isFirstInGroup = col.headerGroup && col.headerGroup !== lastGrp;
                    if (isFirstInGroup) {
                      subtotalCells.push(
                        <div key={`st-spacer-${col.headerGroup}`} style={{
                          width: 18, minWidth: 18, maxWidth: 18,
                          borderRight: isCollapsed ? '1px solid #dee2e6' : 'none',
                          boxSizing: 'border-box', backgroundColor: col.headerColor || '#f8f9fa',
                        }} />
                      );
                    }
                    lastGrp = col.headerGroup;
                    if (isCollapsed) return;
                    const colWidth = getColumnWidth(col.id, col.width);
                    const isProgress = col.type === 'progress' || col.type === 'progress-readonly';
                    const isNumericText = col.accessor === 'length1' || col.accessor === 'size1';
                    const isText = !isProgress && !isNumericText && (col.type === 'text' || col.type === 'select' || col.type === 'fabrication-shop' || col.type === 'link' || col.type === 'date' || col.type === 'priority');
                    const val = subtotalData[col.accessor];
                    const bgColor = col.headerColor || '#f8f9fa';
                    subtotalCells.push(
                      <div key={col.id} style={{
                        width: colWidth, minWidth: colWidth, maxWidth: colWidth,
                        padding: '6px 4px', fontSize: '11px', fontWeight: '700',
                        textAlign: 'center', backgroundColor: bgColor,
                        borderRight: '1px solid #dee2e6', boxSizing: 'border-box',
                        color: isProgress ? (val >= 100 ? '#047857' : val >= 50 ? '#92400e' : '#1e293b') : isNumericText ? '#1e293b' : '#6b7280',
                      }}>
                        {isProgress ? (val == null ? 'N/A' : `${val}%`) : isNumericText && val ? `${val}` : isText && val ? `${val}` : ''}
                      </div>
                    );
                  });
                  subtotalCells.push(<div key="st-scrollbar-spacer" style={{ width: 16, minWidth: 16, maxWidth: 16, boxSizing: 'border-box', backgroundColor: '#f8f9fa' }} />);
                  return subtotalCells;
                })()}
              </div>
            )}
          </div>
        );
      })()}

      {state.toast.show && (
        <Toast
          message={state.toast.message}
          type={state.toast.type}
          onClose={() => dispatch(actions.hideToast())}
        />
      )}

      {/* Bulk tag picker — applies/removes a tag on every selected row */}
      {bulkTagAnchor && tagEntityType && (
        <TagPicker
          entityType={tagEntityType}
          projectId={numericProjectId}
          selectedTags={bulkSelectedTags}
          anchorRect={bulkTagAnchor}
          title={`Tag ${state.ui.selectedRows.size} selected row${state.ui.selectedRows.size === 1 ? '' : 's'}`}
          onClose={() => setBulkTagAnchor(null)}
          onToggle={(tag, nowSelected) => {
            const ids = [...state.ui.selectedRows];
            if (nowSelected) assignEntityTag(tag, ids);
            else unassignEntityTag(tag, ids);
          }}
          onViewUsage={(tag) => setUsageTagId(tag.id)}
        />
      )}

      {/* Filter-by-tag picker — checked tags drive the rows filter (any match) */}
      {tagFilterAnchor && tagEntityType && (
        <TagPicker
          entityType={tagEntityType}
          projectId={numericProjectId}
          selectedTags={tagFilterIds.map((id) => ({ id }))}
          anchorRect={tagFilterAnchor}
          title="Show rows with any of these tags"
          onClose={() => setTagFilterAnchor(null)}
          onToggle={(tag, nowSelected) => {
            setTagFilterIds((prev) => (nowSelected ? [...prev, tag.id] : prev.filter((id) => id !== tag.id)));
          }}
          onViewUsage={(tag) => setUsageTagId(tag.id)}
        />
      )}

      {/* Global tag explorer — everything in the portal carrying one tag */}
      {usageTagId != null && (
        <TagUsageModal tagId={usageTagId} onClose={() => setUsageTagId(null)} />
      )}

      {/* Analytics Dashboard */}
      {showAnalytics && disciplineId === 'piping-nde' && (
        <AnalyticsErrorBoundary onClose={() => setShowAnalytics(false)}>
          <NdeAnalytics
            isOpen={showAnalytics}
            onClose={() => setShowAnalytics(false)}
            projectId={projectId}
          />
        </AnalyticsErrorBoundary>
      )}
      {showAnalytics && disciplineId === 'piping-testing' && (
        <AnalyticsErrorBoundary onClose={() => setShowAnalytics(false)}>
          <PipeTestingAnalytics
            isOpen={showAnalytics}
            onClose={() => setShowAnalytics(false)}
            projectId={projectId}
          />
        </AnalyticsErrorBoundary>
      )}
      {showAnalytics && disciplineId !== 'piping-nde' && disciplineId !== 'piping-testing' && (
        <AnalyticsErrorBoundary onClose={() => setShowAnalytics(false)}>
          <ProgressAnalyticsDashboard
            data={state.data.rows}
            disciplineName={config.name}
            disciplineId={disciplineId}
            projectId={projectId}
            projectDetails={projectDetails}
            onClose={() => setShowAnalytics(false)}
          />
        </AnalyticsErrorBoundary>
      )}

      {/* Progress Tracker Audit Log Modal (all disciplines except NDE) */}
      {showProgressAudit && (
        <ProgressTrackerAuditLogModal
          isOpen={showProgressAudit}
          onClose={() => setShowProgressAudit(false)}
          projectId={projectId}
          discipline={
            config.id === 'mechanical' || ['vessels', 'chutes', 'conveyors', 'skids'].includes(config.id)
              ? 'mechanical,vessels,chutes,conveyors,skids'
              : config.id
          }
          disciplineLabel={
            config.id === 'mechanical' || ['vessels', 'chutes', 'conveyors', 'skids'].includes(config.id)
              ? 'Mechanical (all types)'
              : config.name
          }
          onNavigateToRow={handleNavigateToRow}
          onReverted={() => fetchMatrixData()}
        />
      )}

      {/* Help Modal */}
      {showHelp && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '95%',
            maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            position: 'relative', zIndex: 1,
          }}>
            {/* Header */}
            <div style={{
              padding: '1rem 1.5rem', borderBottom: '1px solid #dee2e6',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              backgroundColor: '#f8f9fa', flexShrink: 0, position: 'relative', zIndex: 3,
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
                {disciplineId === 'piping-nde' ? 'NDE Tracker - Help'
                  : disciplineId === 'piping-testing' ? 'Testing Tracker - Help'
                  : `${config.name} Progress Tracker - Help`}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton
                  compact
                  guideId={
                    helpTab === 'tour' && (disciplineId === 'piping' || disciplineId === 'piping-nde')
                      ? PIPING_WALKTHROUGH_GUIDE_ID
                      : disciplineId === 'piping-testing' ? 'pipe-testing-tracker-help' : 'discipline-matrix-help'
                  }
                  title={
                    helpTab === 'tour' && (disciplineId === 'piping' || disciplineId === 'piping-nde')
                      ? 'Piping Progress Tracker — New Employee Tour'
                      : disciplineId === 'piping-testing' ? 'Testing Tracker Help' : 'Progress Tracker Help'
                  }
                  accent={helpTab === 'tour' ? '#0369a1' : disciplineId === 'piping-testing' ? '#0f766e' : undefined}
                />
                <button onClick={() => { setShowHelp(false); setHelpTab('guide'); }} style={{
                  padding: '8px 16px', border: 'none', borderRadius: '4px',
                  backgroundColor: '#6c757d', color: 'white', cursor: 'pointer', fontSize: '14px'
                }}>Close</button>
              </div>
            </div>

            {/* Tab Bar — flexShrink + zIndex keep tabs above scrollable body */}
            <div style={{
              display: 'flex', flexShrink: 0, position: 'relative', zIndex: 3,
              borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa',
              overflowX: 'auto', overflowY: 'hidden',
            }}>
              {((disciplineId === 'piping-nde' || disciplineId === 'piping-testing') ? [
                { id: 'guide', label: 'User Guide' },
                { id: 'columns', label: 'All Columns' },
                { id: 'tour', label: 'Video Tour' },
              ] : disciplineId === 'piping' ? [
                { id: 'guide', label: 'User Guide' },
                { id: 'columns', label: 'All Columns' },
                { id: 'subtotals', label: 'Subtotals' },
                { id: 'formulas', label: 'Formulas & Calculations' },
                { id: 'workflow', label: 'Workflow Flowchart' },
                { id: 'tour', label: 'Video Tour' },
              ] : [
                { id: 'guide', label: 'User Guide' },
                { id: 'columns', label: 'All Columns' },
                { id: 'subtotals', label: 'Subtotals' },
                { id: 'formulas', label: 'Formulas & Calculations' },
                { id: 'workflow', label: 'Workflow Flowchart' },
                { id: 'tour', label: 'Video Tour' },
              ]).map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setHelpTab(tab.id)}
                  style={{
                    padding: '12px 18px', border: 'none', flexShrink: 0,
                    borderBottom: helpTab === tab.id ? '3px solid #0369a1' : '3px solid transparent',
                    backgroundColor: helpTab === tab.id ? 'white' : 'transparent',
                    color: helpTab === tab.id ? '#0369a1' : '#6b7280',
                    fontWeight: helpTab === tab.id ? '600' : '400',
                    cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap',
                    position: 'relative', zIndex: 1,
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div style={{
              padding: '1.5rem',
              overflowY: 'auto', overflowX: 'hidden',
              flex: 1, minHeight: 0, lineHeight: '1.7',
              background: 'white',
              position: 'relative', zIndex: 1,
            }}>

              {/* ========== GUIDE TAB ========== */}
              {helpTab === 'guide' && disciplineId === 'piping-nde' && (<>
                {/* NDE Overview */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>1</span>
                    What Is the NDE Tracker?
                  </h3>
                  <p style={{ margin: '0 0 12px 0' }}>
                    The <strong>NDE (Non-Destructive Examination) Tracker</strong> records all NDE activities performed on piping welds.
                    It tracks which welds were examined, the examination type (X-Ray, LPI, MPI, UT, VT, PMI), and the results 
                    (pass/fail quantities). This data supports QC documentation and project NDE compliance reporting.
                  </p>
                  <p style={{ margin: '0 0 12px 0' }}>
                    NDE records can be entered manually, imported from the piping progress tracker (to pull ISO sheet numbers and 
                    line numbers), or imported from an Excel file.
                  </p>
                </div>

                {/* Column Reference */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>2</span>
                    Column Reference
                  </h3>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Column</th>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>What It Means</th>
                          <th style={{ textAlign: 'center', padding: '8px', color: '#374151' }}>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Date</td>
                          <td style={{ padding: '8px' }}>Date and time the NDE examination was performed or recorded</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Modified By</td>
                          <td style={{ padding: '8px' }}>User who last edited this record</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>System</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Welder #</td>
                          <td style={{ padding: '8px' }}>Welder identification number from the welder log</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual (dropdown)</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>NDE Type</td>
                          <td style={{ padding: '8px' }}>Type of examination: X-Ray, LPI, MPI, UT, VT, or PMI</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual (dropdown)</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>ISO Sheet #</td>
                          <td style={{ padding: '8px' }}>ISO sheet number from the piping tracker identifying the weld location</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#15803d' }}>Import / Manual</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Line Number</td>
                          <td style={{ padding: '8px' }}>Line number associated with the weld being examined</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#15803d' }}>Import / Manual</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Fluid Code</td>
                          <td style={{ padding: '8px' }}>Fluid service classification that determines NDE requirements</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#15803d' }}>Auto (from tracker)</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Qty Joint</td>
                          <td style={{ padding: '8px' }}>Number of joints examined in this record</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Qty NDE</td>
                          <td style={{ padding: '8px' }}>Number of NDE examinations performed</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Qty Failed</td>
                          <td style={{ padding: '8px' }}>Number of examinations that failed (requires re-weld/re-examine)</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Comments</td>
                          <td style={{ padding: '8px' }}>Free-text notes about the examination</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Features & Tips */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>3</span>
                    Features & Tips
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Import from Tracker</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Pull ISO sheet numbers and line numbers directly from the piping progress tracker to create NDE records</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Drag Fill</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Click a cell, then drag the blue handle down to copy the value to multiple rows at once</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Duplicate Records</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Select rows using checkboxes, then click Duplicate to create copies with the same data</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Export</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Download all NDE records as an Excel file for offline review or reporting</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Audit Log</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>View a complete history of all changes made to NDE records, including who changed what and when</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Analytics</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>View NDE pass/fail rates, examination counts by type, and welder performance summaries</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Filters</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Click the filter icon on any column to filter by welder, NDE type, fluid code, etc.</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Columns</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Hide/show and reorder columns. Each user&apos;s configuration is saved per-project</div>
                    </div>
                  </div>
                </div>

                {/* Common NDE Fluid Codes */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>4</span>
                    Common Fluid Codes That Require NDE
                  </h3>
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#374151' }}>
                    The following fluid codes / service classifications are the most common ones that typically require Non-Destructive Examination.
                    The specific NDE type and percentage required depend on the project specifications, code requirements (e.g., ASME B31.3, CSA Z662), and the service category.
                  </p>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151', width: '90px' }}>Code</th>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Service / Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { code: 'CAV1', desc: 'Category V (Normal) — Low-pressure non-critical services (water, air). Minimal NDE typically required.' },
                          { code: 'CAX1', desc: 'Category X (Severe Cyclic) — Subject to severe cyclic conditions. Requires 100% radiography or UT.' },
                          { code: 'CAX2', desc: 'Category X, Variant 2 — Similar to CAX1 with additional examination scope per specification.' },
                          { code: 'CC1', desc: 'Category C — Corrosive service. NDE extent depends on corrosion allowance and material.' },
                          { code: 'DF2', desc: 'Category D (Flammable), Variant 2 — Flammable fluid service with elevated NDE requirements.' },
                          { code: 'DP2', desc: 'Category D (Pressure), Variant 2 — High-pressure service requiring radiography / UT inspection.' },
                          { code: 'DP3', desc: 'Category D (Pressure), Variant 3 — Highest pressure category with full NDE examination.' },
                          { code: 'DS1', desc: 'Category D (Steam), Variant 1 — Steam service. NDE required per ASME B31.3 or project spec.' },
                          { code: 'DS3', desc: 'Category D (Steam), Variant 3 — High-pressure / high-temperature steam with full NDE.' },
                          { code: 'DZ1', desc: 'Category D (Hazardous/Toxic) — Toxic or lethal fluid service. Typically requires 100% NDE.' },
                          { code: 'DZ2', desc: 'Category D (Hazardous/Toxic), Variant 2 — Elevated hazard level with stricter examination.' },
                          { code: 'EB2', desc: 'Category E (Elevated Temperature), Variant 2 — High-temperature service above 400°F / 200°C.' },
                          { code: 'EC4', desc: 'Category E (Corrosive), Variant 4 — Elevated-temperature corrosive service with full NDE.' },
                          { code: 'EC5', desc: 'Category E (Corrosive), Variant 5 — Most severe corrosive condition at elevated temperature.' },
                          { code: 'WAD', desc: 'Water (Atmospheric Drain) — Atmospheric drain lines; NDE may be required per project spec.' },
                        ].map(({ code, desc }) => (
                          <tr key={code} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px', fontWeight: '700', fontFamily: 'monospace', color: '#0369a1' }}>{code}</td>
                            <td style={{ padding: '8px' }}>{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                    This is a general reference. Always consult the project-specific NDE requirements and piping spec to confirm the exact examination type and percentage for each fluid code.
                  </p>
                </div>

                {/* NDE Types Reference */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>5</span>
                    NDE Examination Types
                  </h3>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151', width: '90px' }}>Type</th>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { type: 'X-Ray', desc: 'Radiographic Testing — Uses X-ray or gamma radiation to detect internal weld defects. Most common for pressure piping.' },
                          { type: 'LPI', desc: 'Liquid Penetrant Inspection — Surface-breaking defects detected via dye penetrant. Used on non-magnetic materials.' },
                          { type: 'MPI', desc: 'Magnetic Particle Inspection — Detects surface/near-surface defects in ferromagnetic materials using magnetic fields.' },
                          { type: 'UT', desc: 'Ultrasonic Testing — Uses high-frequency sound waves to detect internal and surface defects. Alternative to radiography.' },
                          { type: 'VT', desc: 'Visual Testing — Direct visual inspection of weld quality, surface condition, and dimensional accuracy.' },
                          { type: 'PMI', desc: 'Positive Material Identification — Verifies the chemical composition of materials matches specifications.' },
                        ].map(({ type, desc }) => (
                          <tr key={type} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px', fontWeight: '700', fontFamily: 'monospace', color: '#0369a1' }}>{type}</td>
                            <td style={{ padding: '8px' }}>{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>)}

              {helpTab === 'guide' && disciplineId === 'piping-testing' && (<>
                {/* Testing Tracker Overview */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0f766e', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>1</span>
                    What Is the Testing Tracker?
                  </h3>
                  <p style={{ margin: '0 0 12px 0' }}>
                    The <strong>Testing Tracker</strong> records ASME B31.3 / B31.1 pressure testing activities performed on piping systems,
                    organized by fluid code / system rather than by individual weld. It is separate from the NDE Tracker: NDE tracks
                    weld radiography and other examinations, while this tracker records hydrostatic, pneumatic, and leak testing performed
                    on a completed system before it is turned over.
                  </p>
                  <p style={{ margin: '0 0 12px 0' }}>
                    Each row represents one system or line under test: its design conditions, the type of test required, the medium used,
                    the hold time, and the final pass/fail result with inspector sign-off.
                  </p>
                </div>

                {/* Column Reference */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0f766e', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>2</span>
                    Column Reference
                  </h3>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Column</th>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>What It Means</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['Line / System ID', 'Identifier for the line or system being tested'],
                          ['Fluid Code', 'Fluid service classification for the system'],
                          ['System Description', 'Free-text description of the system / service'],
                          ['Design Code', 'Governing design code, e.g. ASME B31.3 or B31.1'],
                          ['Design Press. (PSI)', 'Design pressure rating of the system'],
                          ['Design Temp. (°F)', 'Design temperature rating of the system'],
                          ['Test Press. (PSI)', 'Required test pressure (may reference "Operating Pressure" instead of a fixed value)'],
                          ['Type of Testing', 'Hydrostatic, Pneumatic, Service Leak, Sensitive Leak, or Alternative Leak'],
                          ['Test Medium', 'Water (Filtered), Dry Nitrogen, Clean Compressed Air, Service Fluid, or Helium/Air Mixture'],
                          ['Hold Time (Min)', 'Minutes the test pressure must be held per code / spec'],
                          ['Completed / Status', 'Pending, In Progress, Pass / Complete, or Failed'],
                          ['Test Date', 'Date the test was performed'],
                          ['Inspector / Welder', 'Person who performed or witnessed the test'],
                          ['Sign-off', 'Name/initials of the person accepting the test result'],
                          ['Notes / Comments', 'Free text notes about the test'],
                        ].map(([col, desc]) => (
                          <tr key={col} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px', fontWeight: '600' }}>{col}</td>
                            <td style={{ padding: '8px' }}>{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Status Workflow */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0f766e', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>3</span>
                    Status Workflow
                  </h3>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {[
                      { label: 'Pending', bg: '#fef3c7', color: '#92400e' },
                      { label: 'In Progress', bg: '#dbeafe', color: '#1e40af' },
                      { label: 'Pass / Complete', bg: '#dcfce7', color: '#166534' },
                      { label: 'Failed', bg: '#fee2e2', color: '#991b1b' },
                    ].map((s, i, arr) => (
                      <React.Fragment key={s.label}>
                        <span style={{ padding: '6px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '700', backgroundColor: s.bg, color: s.color }}>
                          {s.label}
                        </span>
                        {i < arr.length - 1 && <span style={{ color: '#9ca3af' }}>→</span>}
                      </React.Fragment>
                    ))}
                  </div>
                  <p style={{ margin: '12px 0 0 0', fontSize: '13px', color: '#374151' }}>
                    Every system starts <strong>Pending</strong>. Once testing begins, mark it <strong>In Progress</strong>. When the hold time
                    is met with no pressure drop, mark it <strong>Pass / Complete</strong> and record the test date, inspector, and sign-off.
                    If the system does not hold pressure or otherwise fails, mark it <strong>Failed</strong> — repairs and a retest can be
                    tracked by duplicating the row (which copies the system's design/spec fields) and starting a fresh Pending record.
                  </p>
                </div>

                {/* Features & Tips */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0f766e', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>4</span>
                    Features & Tips
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div style={{ padding: '12px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                      <div style={{ fontWeight: '600', color: '#0f766e', marginBottom: '4px' }}>Import from Fluid Codes</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Seed new Pending rows straight from the distinct fluid codes already in the Progress Tracker</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                      <div style={{ fontWeight: '600', color: '#0f766e', marginBottom: '4px' }}>Duplicate Records</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Select rows using checkboxes, then click Duplicate to copy a system's spec fields into a fresh retest row</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                      <div style={{ fontWeight: '600', color: '#0f766e', marginBottom: '4px' }}>Export</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Download the full testing log as Excel for offline review or client reporting</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                      <div style={{ fontWeight: '600', color: '#0f766e', marginBottom: '4px' }}>Audit Log</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>View a complete history of every change made to testing records, including who and when</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                      <div style={{ fontWeight: '600', color: '#0f766e', marginBottom: '4px' }}>Analytics</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>View completion percentage, status breakdown, and results by test type and fluid code</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                      <div style={{ fontWeight: '600', color: '#0f766e', marginBottom: '4px' }}>Filters</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Click the filter icon on any column to narrow down by status, test type, or fluid code</div>
                    </div>
                  </div>
                </div>

                {/* ASME Quick Reference */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0f766e', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>5</span>
                    ASME B31.3 / B31.1 Quick Reference
                  </h3>
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#374151' }}>
                    General guidance from Dylan Picotte's (QC) reference sheet. Always confirm exact requirements against the project
                    piping specification and code of record.
                  </p>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151', width: '150px' }}>Test Type</th>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Typical Use</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { type: 'Hydrostatic', desc: 'Default test per ASME B31.3 clause 345 — system filled with water and pressurized to typically one and a half times design pressure. Preferred for most liquid and low-hazard services.' },
                          { type: 'Pneumatic', desc: 'Used when hydrostatic testing is impractical (e.g., moisture-sensitive service). Tested with dry nitrogen or clean compressed air at a reduced multiplier, with strict safety precautions due to stored energy.' },
                          { type: 'Service Leak', desc: 'A leak test performed at normal operating pressure using the actual service fluid, typically on lower-hazard systems already covered by another pressure test.' },
                          { type: 'Sensitive Leak', desc: 'A more rigorous leak test (e.g., helium mass spectrometry) used for hazardous, toxic, or environmentally critical services where even small leaks are unacceptable.' },
                          { type: 'Alternative Leak', desc: 'Any alternative leak test method accepted by the code and project specification when standard hydrostatic or pneumatic testing is not suitable.' },
                        ].map(({ type, desc }) => (
                          <tr key={type} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px', fontWeight: '700', color: '#0f766e' }}>{type}</td>
                            <td style={{ padding: '8px' }}>{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>)}

              {helpTab === 'guide' && disciplineId !== 'piping-nde' && disciplineId !== 'piping-testing' && (<>
                {/* Overview */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>1</span>
                    What Is This Tracker?
                  </h3>
                  <p style={{ margin: '0 0 12px 0' }}>
                    The <strong>{config.name}</strong> progress tracker is the central tool for monitoring construction progress on every line item. 
                    It tracks both <strong>shop fabrication</strong> and <strong>field installation</strong> activities, and auto-calculates 
                    key project metrics: <strong>Credit</strong>, <strong>Earned Hours</strong>, <strong>Earned Credit</strong>, and <strong>Total Field Completion</strong>.
                  </p>
                  <p style={{ margin: '0 0 12px 0' }}>
                    These calculated values flow directly into the <strong>Earned Report</strong>, <strong>Budget Report</strong>, <strong>EVM Report</strong>, and <strong>SOV Report</strong>. 
                    Any change you make to a progress step here automatically updates all downstream reports in real time.
                  </p>
                </div>

                {/* Column Reference */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>2</span>
                    Column Reference
                  </h3>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Column</th>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>What It Means</th>
                          <th style={{ textAlign: 'center', padding: '8px', color: '#374151' }}>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Line Number / ISO</td>
                          <td style={{ padding: '8px' }}>Unique identifier for each line item or ISO in the project</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#15803d' }}>Excel Import</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Area / System</td>
                          <td style={{ padding: '8px' }}>Physical location and system classification for filtering</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#15803d' }}>Excel Import</td>
                        </tr>
                        {disciplineId === 'piping' && (<>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Size 1/2/3, Length 1/2/3</td>
                            <td style={{ padding: '8px' }}>Pipe dimensions - used to calculate Credit via the sqrt formula</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#15803d' }}>Excel Import</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Shop/Field Ratio</td>
                            <td style={{ padding: '8px' }}>Percentage split between shop fabrication and field installation (e.g., 60% shop / 40% field)</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Configurable</td>
                          </tr>
                        </>)}
                        <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f0f9ff' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Progress Steps</td>
                          <td style={{ padding: '8px' }}>Individual completion percentages (0 / 25 / 50 / 75 / 100%) for each phase of work</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual (dropdown)</td>
                        </tr>
                        {disciplineId === 'piping' && (<>
                          <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f0f9ff' }}>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Total Shop Completion</td>
                            <td style={{ padding: '8px' }}>
                              Shop fabrication progress. Field Run pipes have no shop fab — this cell shows N/A and is left out of overall fab %. If a pipe is fabricated on site, set the shop to Site so it stays in fabrication. Values represent specific stages:
                              <div style={{ marginTop: '4px', fontSize: '12px', lineHeight: '1.6' }}>
                                <span style={{ display: 'inline-block', backgroundColor: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', marginRight: '6px' }}><strong>0%</strong> — Not started</span>
                                <span style={{ display: 'inline-block', backgroundColor: '#fef9c3', padding: '1px 6px', borderRadius: '4px', marginRight: '6px' }}><strong>35%</strong> — Boxed &amp; Cut</span>
                                <span style={{ display: 'inline-block', backgroundColor: '#fde68a', padding: '1px 6px', borderRadius: '4px', marginRight: '6px' }}><strong>40%</strong> — Issued to Bench</span>
                                <span style={{ display: 'inline-block', backgroundColor: '#d1fae5', padding: '1px 6px', borderRadius: '4px', marginRight: '6px' }}><strong>65%</strong> — Tacked</span>
                                <span style={{ display: 'inline-block', backgroundColor: '#dbeafe', padding: '1px 6px', borderRadius: '4px', marginRight: '6px' }}><strong>95%</strong> — Welded (ready to ship)</span>
                                <span style={{ display: 'inline-block', backgroundColor: '#e0e7ff', padding: '1px 6px', borderRadius: '4px' }}><strong>100%</strong> — Loaded (shipped)</span>
                              </div>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual (dropdown)</td>
                          </tr>
                        </>)}
                        <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#fffbeb' }}>
                          <td style={{ padding: '8px', fontWeight: '700', color: '#92400e' }}>Total Field Completion</td>
                          <td style={{ padding: '8px' }}>Weighted average of all progress steps based on Rules of Credit weights (see Formulas tab)</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#0369a1', fontWeight: '600' }}>Auto-Calculated</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Budget Hours</td>
                          <td style={{ padding: '8px' }}>Total budgeted hours for this line item{disciplineId === 'piping' ? ' (from import)' : ''}</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: disciplineId === 'piping' ? '#15803d' : '#6b7280' }}>{disciplineId === 'piping' ? 'Excel Import' : 'Manual'}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#fffbeb' }}>
                          <td style={{ padding: '8px', fontWeight: '700', color: '#92400e' }}>Credit (Budget Credit)</td>
                          <td style={{ padding: '8px' }}>{disciplineId === 'piping'
                            ? 'Represents the complexity/weight of a line item. Auto-calculated from pipe dimensions using the sqrt formula'
                            : 'Represents the complexity/weight of a line item. Entered manually to assign a credit value for earned calculations'
                          }</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: disciplineId === 'piping' ? '#0369a1' : '#6b7280', fontWeight: '600' }}>{disciplineId === 'piping' ? 'Auto-Calculated' : 'Manual'}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#fffbeb' }}>
                          <td style={{ padding: '8px', fontWeight: '700', color: '#92400e' }}>Earned Hours</td>
                          <td style={{ padding: '8px' }}>{disciplineId === 'piping'
                            ? 'Hours earned based on completion progress. Calculated separately for shop and field using Budget Hours x Completion % x Shop/Field Ratio'
                            : 'Hours earned based on completion progress: Budget Hours x Total Field Completion / 100'
                          }</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#0369a1', fontWeight: '600' }}>Auto-Calculated</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#fffbeb' }}>
                          <td style={{ padding: '8px', fontWeight: '700', color: '#92400e' }}>Earned Credit</td>
                          <td style={{ padding: '8px' }}>{disciplineId === 'piping'
                            ? 'Credit earned based on completion progress. Calculated separately for shop and field using Credit x Completion % x Shop/Field Ratio'
                            : 'Credit earned based on completion progress: Credit x Total Field Completion / 100'
                          }</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#0369a1', fontWeight: '600' }}>Auto-Calculated</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Hold</td>
                          <td style={{ padding: '8px' }}>Flags a line item as on-hold. Held items are excluded from certain report calculations</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual (checkbox)</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>Comments</td>
                          <td style={{ padding: '8px' }}>Free-text notes for any line item. Supports @mentions for team collaboration</td>
                          <td style={{ padding: '8px', textAlign: 'center', color: '#6b7280' }}>Manual</td>
                        </tr>
                        {disciplineId === 'piping' && (<>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px', fontWeight: '600' }}>ISO Ver / MTO Rev</td>
                            <td style={{ padding: '8px' }}>Engineering ISO version and the MTO revision that produced this tracker line (from awarded-estimate auto-push)</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#0369a1', fontWeight: '600' }}>Auto (MTO)</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Superseded (struck-through)</td>
                            <td style={{ padding: '8px' }}>Old ISO version kept for history when a newer version is pushed. Greyed + line-through, read-only; progress is preserved but excluded from live earned / EVM / SOV totals</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#0369a1', fontWeight: '600' }}>Auto (MTO)</td>
                          </tr>
                        </>)}
                      </tbody>
                    </table>
                  </div>
                </div>

                {disciplineId === 'piping' && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>2b</span>
                      ISO Re-issues &amp; Auto Change Orders
                    </h3>
                    <p style={{ margin: '0 0 12px 0' }}>
                      After the estimate is <strong>awarded</strong>, each MTO revision auto-pushes into this tracker. When an ISO is re-issued at a <strong>new version</strong>, the prior line is <strong>struck through</strong> (kept for baseline history) and a <strong>new Extra line</strong> is appended with the new quantities. If the old line already had shop/field progress, the new active line is put <strong>ON HOLD</strong>.
                    </p>
                    <p style={{ margin: '0 0 8px 0' }}>
                      Change Orders Tracking then receives <strong>up to two automatic CCRs</strong>:
                    </p>
                    <ul style={{ margin: '0 0 0 1.2rem', padding: 0 }}>
                      <li style={{ marginBottom: '6px' }}><strong>Scope / quantity CO</strong> — MTO labour-hour and quantity deltas (new ISOs, new versions, qty increases).</li>
                      <li><strong>Earned-hours CO</strong> — a separate CCR that claims earned hours from the just-superseded line, so work already progressed is not lost when live reports exclude struck-through rows.</li>
                    </ul>
                  </div>
                )}

                {/* Where Do These Numbers Go? */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>3</span>
                    Where Do These Numbers Go?
                  </h3>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Report</th>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Uses From This Tracker</th>
                          <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>What It Shows</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600', color: '#0369a1' }}>Earned Report</td>
                          <td style={{ padding: '8px' }}>Earned Hours (shop + field), Earned Credit over time</td>
                          <td style={{ padding: '8px' }}>Weekly/monthly earned hours and credit curves, cumulative progress over time</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600', color: '#0369a1' }}>Budget Report</td>
                          <td style={{ padding: '8px' }}>Budget Hours, schedule baseline</td>
                          <td style={{ padding: '8px' }}>Planned vs actual budget distribution, S-curves</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontWeight: '600', color: '#0369a1' }}>EVM Report</td>
                          <td style={{ padding: '8px' }}>Earned Hours, Budget Hours, Actual Cost</td>
                          <td style={{ padding: '8px' }}>Earned Value Management metrics: CPI, SPI, CV, SV, EAC</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', fontWeight: '600', color: '#0369a1' }}>SOV Report</td>
                          <td style={{ padding: '8px' }}>Earned Credit, Total Field Completion</td>
                          <td style={{ padding: '8px' }}>Schedule of Values breakdown by area, system, or activity</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p style={{ margin: '12px 0 0 0', color: '#dc2626', fontSize: '13px', fontWeight: '600' }}>
                    Important: Every progress update you make here ripples through all these reports. Accurate progress entry is critical for project reporting.
                  </p>
                </div>

                {/* Features & Tips */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>4</span>
                    Features & Tips
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Drag Fill</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Click a cell, then drag the blue handle down to copy the value to multiple rows at once</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Columns</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Hide/show, rename, and reorder columns. Each user&apos;s configuration is saved per-project</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Filters</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Click the filter icon on any column header to filter rows by specific values (area, system, etc.)</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Export</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Download as Excel: &quot;Dashboard&quot; exports visible columns, &quot;All Columns&quot; exports everything including hidden fields</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                      <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>Import Template</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>The template has ~40 source-data columns (line info, sizing, budget, WBS). Progress columns, calculated columns, UDFs, and system columns are excluded — they are managed automatically by the tracker</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Hold</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Check the Hold box to flag an ISO as on-hold. Held items are visually marked and excluded from some report calculations</div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Real-Time Sync</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Multiple users can edit simultaneously. Changes appear in real-time with presence indicators showing who is viewing</div>
                    </div>
                    {disciplineId === 'piping' && (
                      <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                        <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Rules of Credit</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>Configure per-project weights for how much each progress step contributes to Total Field Completion</div>
                      </div>
                    )}
                    <div style={{ padding: '12px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                      <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>🔒 Freeze Rules of Credit (required before progress)</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Progress entry is locked for a discipline until its Rules of Credit are <strong>frozen</strong>. A Project Manager
                        or Technical Coordinator opens the Rules of Credit window, reviews the percentages (for equipment, use the
                        “Received by LGL” toggle to pick the established preset), confirms the total is 100%, then clicks
                        <strong> Freeze for Project</strong>. Once frozen, the weights are locked for the life of the project and only a
                        superadmin can unfreeze them to make corrections.
                      </div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Analytics</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Open the Analytics dashboard for visual charts and progress breakdowns by area, system, and activity</div>
                    </div>
                  </div>
                </div>

                {/* History & Snapshots */}
                <div>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>5</span>
                    History & Snapshots
                  </h3>
                  <p style={{ margin: 0 }}>
                    The portal automatically saves periodic snapshots of all tracker data. These history records power the time-based reports 
                    (Earned Report, Budget Report, EVM) and allow you to see how progress has changed over weeks and months. 
                    Historical earned hours and earned credit are preserved even if future edits change current values.
                  </p>
                </div>
              </>)}

              {/* ========== ALL COLUMNS TAB ========== */}
              {helpTab === 'columns' && (<>
                <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '13px' }}>
                  Complete list of every column in the <strong>{config.name}</strong> tracker. Source shows where the data comes from.
                </p>
                <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0, backgroundColor: '#f1f5f9' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#374151' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#374151' }}>Column</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#374151' }}>DB Field</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', color: '#374151' }}>Source</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#374151' }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const srcBadge = (label, color) => (
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: color, color: 'white', whiteSpace: 'nowrap' }}>{label}</span>
                        );
                        const SRC = {
                          import: srcBadge('Excel Import', '#15803d'),
                          manual: srcBadge('Manual', '#2563eb'),
                          calc: srcBadge('Auto-Calculated', '#9333ea'),
                          system: srcBadge('System', '#6b7280'),
                          config: srcBadge('Configurable', '#d97706'),
                        };
                        const rowStyle = (idx) => ({ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' });
                        const td = { padding: '5px 8px' };
                        const tdc = { ...td, textAlign: 'center' };

                        const pipingCols = [
                          ['ISO Sheet Number', 'iso_sheet_number', SRC.import, 'Primary identifier for the line item'],
                          ['Hold', 'on_hold', SRC.manual, 'Checkbox to flag ISO as on-hold'],
                          ['Fluid Code', 'fluid_code', SRC.import, 'Fluid type classification'],
                          ['P&ID', 'pnid', SRC.import, 'Piping & Instrumentation Diagram reference'],
                          ['Priority', 'priority', SRC.manual, 'Dropdown from priority list'],
                          ['Size 1', 'size_1', SRC.import, 'Primary pipe diameter — used in credit formula'],
                          ['Material 1', 'material_1', SRC.import, 'Primary pipe material'],
                          ['Schedule 1', 'schedule_1', SRC.import, 'Pipe wall thickness schedule'],
                          ['Length 1', 'length_1', SRC.import, 'Primary pipe length — used in credit formula'],
                          ['Size 2 / Length 2', 'size_2, length_2', SRC.import, 'Second pipe segment (hidden) — used in credit formula'],
                          ['Size 3 / Length 3', 'size_3, length_3', SRC.import, 'Third pipe segment (hidden) — used in credit formula'],
                          ['Fabrication Shop', 'fabrication_shop', SRC.manual, 'Dropdown from fabrication locations list'],
                          ['Total Shop Completion', 'total_shop_completion', SRC.manual, '0=Not started, 35=Boxed & Cut, 40=Issued to Bench, 65=Tacked, 95=Welded (ready to ship), 100=Loaded (shipped)'],
                          ['Load Number', 'load_number', SRC.manual, 'Shipping load reference'],
                          ['Date Shipped', 'date_shipped', SRC.manual, 'Date shipped from fab shop'],
                          ['Received', 'received', SRC.manual, 'Material received on site (0/100%)'],
                          ['Transport to Install Area', 'transport_to_installation_area', SRC.manual, 'Field step (0/25/50/75/100%) — RoC weighted'],
                          ['Erect Supports/Hangers', 'erect_supports_hangers', SRC.manual, 'Field step (0/25/50/75/100%) — RoC weighted'],
                          ['Fit Up', 'fit_up', SRC.manual, 'Field step (0/25/50/75/100%) — RoC weighted'],
                          ['Bolt / Weld', 'bolt_up_weld', SRC.manual, 'Field step (0/25/50/75/100%) — RoC weighted'],
                          ['Line Cleanup / Hydro Test', 'line_cleanup_hydro_test', SRC.manual, 'Field step (0/25/50/75/100%) — RoC weighted'],
                          ['Heat Tracing', 'heat_tracing', SRC.manual, 'Optional field step (0/25/50/75/100%)'],
                          ['Insulation', 'insulation', SRC.manual, 'Optional field step (0/25/50/75/100%)'],
                          ['Total Field Completion', 'field_total_completion', SRC.calc, 'Weighted sum of field steps x RoC weights'],
                          ['Shop/Field Ratio', 'shop_field_ratio', SRC.config, 'Project-level split (e.g., 60/40) for hours/credit'],
                          ['Budget Hours', 'budget_hours', SRC.import, 'Total budgeted hours for this line'],
                          ['Credit (Budget Credit)', 'budget_credit', SRC.calc, 'sqrt(S1×L1) + sqrt(S2×L2) + sqrt(S3×L3)'],
                          ['Earned Hours (Shop)', 'earned_hours_shop', SRC.calc, 'Budget Hr × (Ratio/100) × (Shop Comp/100)'],
                          ['Earned Hours (Field)', 'earned_hours_field', SRC.calc, 'Budget Hr × (1−Ratio/100) × (Field Comp/100)'],
                          ['Earned Credit (Shop)', 'earned_credit_shop', SRC.calc, 'Credit × (Ratio/100) × (Shop Comp/100)'],
                          ['Earned Credit (Field)', 'earned_credit_field', SRC.calc, 'Credit × (1−Ratio/100) × (Field Comp/100)'],
                          ['SOV Code', 'sov', SRC.import, 'Schedule of Values reference code'],
                          ['Area', 'area', SRC.import, 'Physical area (hidden, used for filtering)'],
                          ['Sub Area', 'sub_area', SRC.import, 'Sub-area (hidden)'],
                          ['System / Sub-System', 'system, sub_system', SRC.import, 'System classification (hidden)'],
                          ['Activity ID / Name', 'activity_id, activity_name', SRC.import, 'Pipe Install / Mechanical Install schedule link — field % rolls here'],
                          ['Fab Activity ID / Name', 'fab_activity_id, fab_activity_name', SRC.import, 'Pipe Fab schedule link — shop % rolls here (piping)'],
                          ['Line Number', 'line_number', SRC.import, 'Line tag number (hidden)'],
                          ['Cost Code', 'cost_code', SRC.import, 'Cost tracking code (hidden)'],
                          ['Mat / Bore / Mat-Bore', 'mat, bore, mat_bore', SRC.import, 'Material-bore combination (hidden)'],
                          ['Client WBS / LGL WBS', 'client_wbs, lgl_wbs_internal', SRC.import, 'Work breakdown structure codes (hidden)'],
                          ['NDE / NDE Type', 'nde, nde_type', SRC.import, 'Non-destructive examination info'],
                          ['Comments', 'comments', SRC.manual, 'Free text, supports @mentions'],
                          ['UDF 01–10', 'udf01 to udf10', SRC.manual, 'User-defined custom fields'],
                          ['Last Modified By', 'last_modified_by', SRC.system, 'Auto-set on every edit'],
                          ['Created / Updated At', 'created_at, updated_at', SRC.system, 'Timestamps'],
                        ];

                        const nonPipingCols = [
                          ['Equipment Tag / Code', 'equipment_tag_or_code', SRC.import, 'Primary identifier for the item'],
                          ['Hold', 'on_hold', SRC.manual, 'Checkbox to flag item as on-hold'],
                          ['Area', 'area', SRC.import, 'Physical area'],
                          ['Sub Area', 'sub_area', SRC.import, 'Sub-area within area'],
                          ['Elevation / Floor', 'elevation, floor', SRC.import, 'Vertical location'],
                          ['Building Number', 'building_number', SRC.import, 'Building reference'],
                          ['Description', 'description', SRC.import, 'Item description'],
                          ['Drawing Number', 'drawing_number', SRC.import, 'Engineering drawing reference'],
                          ['P&ID', 'p_and_id', SRC.import, 'P&ID reference'],
                          ['Division / Sub-Division', 'division_area, sub_division_area', SRC.import, 'Division classification'],
                          ['Progress Steps', '(varies by discipline)', SRC.manual, 'Each step 0/25/50/75/100% — RoC weighted'],
                          ['Total Field Completion', 'total_field_completion', SRC.calc, 'Weighted sum of progress steps x RoC weights'],
                          ['Budgeted Hours', 'budgeted_hr', SRC.manual, 'Budget hours for this item'],
                          ['Credit', 'credit', SRC.manual, 'Budget credit value (manual entry)'],
                          ['Earned Hours', 'earned_hr', SRC.calc, 'Budgeted Hr × (Field Completion / 100)'],
                          ['Earned Credit', 'earned_credit', SRC.calc, 'Credit × (Field Completion / 100)'],
                          ['Cost Code', 'cost_code', SRC.import, 'Cost tracking code'],
                          ['Comments', 'comments', SRC.manual, 'Free text notes'],
                          ['UDF 01–10', 'udf01 to udf10', SRC.manual, 'User-defined custom fields'],
                          ['Last Modified By', 'last_modified_by', SRC.system, 'Auto-set on every edit'],
                          ['Created / Updated At', 'created_at, updated_at', SRC.system, 'Timestamps'],
                        ];

                        const ndeCols = [
                          ['Date', 'date', SRC.manual, 'Date/time the NDE examination was performed'],
                          ['Modified By', 'modified_by', SRC.system, 'User who last edited this record'],
                          ['Welder #', 'welder_number', SRC.manual, 'Welder ID from welder log (dropdown)'],
                          ['NDE Type', 'nde_type', SRC.manual, 'X-Ray, LPI, MPI, UT, VT, or PMI'],
                          ['ISO Sheet #', 'iso_sheet_number', SRC.manual, 'ISO from piping tracker or manual entry'],
                          ['Line Number', 'line_number', SRC.manual, 'Line number for the examined weld'],
                          ['Fluid Code', 'fluid_code', SRC.system, 'Auto-populated from piping tracker'],
                          ['Qty Joint', 'qty_joint', SRC.manual, 'Number of joints examined'],
                          ['Qty NDE', 'qty_nde', SRC.manual, 'Number of NDE examinations performed'],
                          ['Qty Failed', 'qty_failed', SRC.manual, 'Number of failed examinations'],
                          ['Comments', 'comments', SRC.manual, 'Free text notes'],
                          ['Created / Updated At', 'created_at, updated_at', SRC.system, 'Timestamps'],
                        ];

                        const testingCols = [
                          ['Line / System ID', 'line_system_id', SRC.manual, 'Identifier for the line or system being tested'],
                          ['Fluid Code', 'fluid_code', SRC.manual, 'Fluid service classification for the system'],
                          ['System Description', 'system_description', SRC.manual, 'Free-text description of the system / service'],
                          ['Design Code', 'design_code', SRC.manual, 'Governing design code, e.g. ASME B31.3 / B31.1'],
                          ['Design Press. (PSI)', 'design_press_psi', SRC.manual, 'Design pressure rating of the system'],
                          ['Design Temp. (°F)', 'design_temp_f', SRC.manual, 'Design temperature rating of the system'],
                          ['Test Press. (PSI)', 'test_press_psi', SRC.manual, 'Required test pressure (may be text, e.g. "Operating Pressure")'],
                          ['Type of Testing', 'test_type', SRC.manual, 'Hydrostatic, Pneumatic, Service Leak, Sensitive Leak, or Alternative Leak'],
                          ['Test Medium', 'test_medium', SRC.manual, 'Water (Filtered), Dry Nitrogen, Clean Compressed Air, Service Fluid, or Helium/Air Mixture'],
                          ['Hold Time (Min)', 'hold_time_min', SRC.manual, 'Minutes the test pressure must be held'],
                          ['Completed / Status', 'status', SRC.manual, 'Pending, In Progress, Pass / Complete, or Failed'],
                          ['Test Date', 'test_date', SRC.manual, 'Date the test was performed'],
                          ['Inspector / Welder', 'inspector', SRC.manual, 'Person who performed or witnessed the test'],
                          ['Sign-off', 'sign_off', SRC.manual, 'Name/initials accepting the test result'],
                          ['Notes / Comments', 'notes', SRC.manual, 'Free text notes'],
                          ['Modified By', 'modified_by', SRC.system, 'User who last edited this record'],
                        ];

                        const cols = disciplineId === 'piping-nde' ? ndeCols
                          : disciplineId === 'piping-testing' ? testingCols
                          : (disciplineId === 'piping') ? pipingCols : nonPipingCols;

                        return cols.map((c, idx) => (
                          <tr key={idx} style={rowStyle(idx)}>
                            <td style={{ ...td, color: '#9ca3af', fontSize: '11px' }}>{idx + 1}</td>
                            <td style={{ ...td, fontWeight: '600' }}>{c[0]}</td>
                            <td style={{ ...td, fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>{c[1]}</td>
                            <td style={tdc}>{c[2]}</td>
                            <td style={{ ...td, color: '#6b7280' }}>{c[3]}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px' }}>
                  <span><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#15803d', color: 'white' }}>Excel Import</span> = from uploaded Excel file</span>
                  <span><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#2563eb', color: 'white' }}>Manual</span> = user enters in tracker</span>
                  <span><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#9333ea', color: 'white' }}>Auto-Calculated</span> = database computes automatically</span>
                  <span><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#d97706', color: 'white' }}>Configurable</span> = project-level setting</span>
                  <span><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#6b7280', color: 'white' }}>System</span> = auto-set by portal</span>
                </div>
              </>)}

              {/* ========== SUBTOTALS TAB ========== */}
              {helpTab === 'subtotals' && (<>
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#111827', fontSize: '1.1rem' }}>Subtotal Bar</h3>
                  <p style={{ margin: '0 0 16px 0', color: '#4b5563', fontSize: '13px' }}>
                    The subtotal bar at the bottom of the table shows aggregated values for the visible (filtered or unfiltered) rows. Each column type uses a different formula.
                  </p>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#0369a1' }}>Progress Columns (Shop &amp; Field)</h4>
                  <p style={{ margin: '0 0 8px 0', color: '#4b5563', fontSize: '13px' }}>
                    Progress columns show a <strong>credit-weighted percentage</strong>, not a simple average. This means ISOs with larger budget credit contribute proportionally more to the overall percentage.
                  </p>
                  <div style={{ backgroundColor: '#f0f9ff', borderRadius: '8px', padding: '16px', border: '1px solid #bae6fd', marginBottom: '12px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: '600', color: '#0c4a6e', marginBottom: '4px' }}>Total Shop Completion:</div>
                      <code style={{ fontSize: '13px', backgroundColor: '#e0f2fe', padding: '4px 8px', borderRadius: '4px', color: '#0369a1' }}>
                        Sum(Earned Credit Shop) / Sum(Budget Credit) x 100
                      </code>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: '600', color: '#0c4a6e', marginBottom: '4px' }}>Total Field Completion:</div>
                      <code style={{ fontSize: '13px', backgroundColor: '#e0f2fe', padding: '4px 8px', borderRadius: '4px', color: '#0369a1' }}>
                        Sum(Earned Credit Field) / Sum(Budget Credit) x 100
                      </code>
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: '#0c4a6e', marginBottom: '4px' }}>Individual Field Steps (Transport, Erect, Fit Up, Bolt/Weld, Cleanup, etc.):</div>
                      <code style={{ fontSize: '13px', backgroundColor: '#e0f2fe', padding: '4px 8px', borderRadius: '4px', color: '#0369a1' }}>
                        Sum(Credit x Step%) / Sum(Budget Credit)
                      </code>
                    </div>
                  </div>
                  <p style={{ margin: '0', color: '#6b7280', fontSize: '12px', fontStyle: 'italic' }}>
                    If no budget credit data exists, falls back to a simple average of all row values.
                  </p>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#15803d' }}>Length 1</h4>
                  <div style={{ backgroundColor: '#f0fdf4', borderRadius: '8px', padding: '16px', border: '1px solid #bbf7d0' }}>
                    <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px' }}>Formula:</div>
                    <code style={{ fontSize: '13px', backgroundColor: '#dcfce7', padding: '4px 8px', borderRadius: '4px', color: '#15803d' }}>
                      Sum of all Length 1 values (total footage)
                    </code>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#b45309' }}>Size 1</h4>
                  <div style={{ backgroundColor: '#fffbeb', borderRadius: '8px', padding: '16px', border: '1px solid #fde68a' }}>
                    <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>Formula:</div>
                    <code style={{ fontSize: '13px', backgroundColor: '#fef3c7', padding: '4px 8px', borderRadius: '4px', color: '#b45309' }}>
                      Average of all Size 1 values (average pipe diameter)
                    </code>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#6b7280' }}>Text Columns (Fluid Code, P&amp;ID, Material, Schedule, etc.)</h4>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Formula:</div>
                    <code style={{ fontSize: '13px', backgroundColor: '#f3f4f6', padding: '4px 8px', borderRadius: '4px', color: '#374151' }}>
                      Count of unique (distinct) values
                    </code>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>Row Count</h4>
                  <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb' }}>
                    <p style={{ margin: '0', color: '#4b5563', fontSize: '13px' }}>
                      The first column shows the row count. When no filter is active it shows <strong>"X rows"</strong>. When filters are active it shows <strong>"X / Y"</strong> (filtered rows / total rows).
                    </p>
                  </div>
                </div>

                <div style={{ padding: '16px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#1e40af' }}>Filtering</h4>
                  <p style={{ margin: '0', color: '#1e40af', fontSize: '13px' }}>
                    All subtotal calculations apply only to the <strong>currently visible (filtered) rows</strong>. When you apply column filters, text filters, or a global search, the subtotals recalculate to reflect only the matching rows.
                  </p>
                </div>
              </>)}

              {/* ========== FORMULAS TAB ========== */}
              {helpTab === 'formulas' && (<>
                {/* Discipline-specific formulas */}
                {disciplineId === 'piping' && (<>
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Piping Credit Formula</h3>
                    <div style={{ padding: '20px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '2px solid #f59e0b' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: '700', color: '#92400e', textAlign: 'center', marginBottom: '12px' }}>
                        Credit = sqrt(Size1 x Length1) + sqrt(Size2 x Length2) + sqrt(Size3 x Length3)
                      </div>
                      <p style={{ margin: '0', fontSize: '13px', color: '#78350f' }}>
                        The square root formula accounts for the non-linear relationship between pipe dimensions and installation complexity. 
                        A larger-diameter, longer pipe section requires disproportionately more effort. Each pipe segment (up to 3 per line) 
                        contributes independently. If a segment has no size or length, it contributes 0.
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Total Field Completion</h3>
                    <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '600', color: '#15803d', marginBottom: '12px' }}>
                        Total Field Completion = (Transport x W1) + (Erect x W2) + (Fit-Up x W3) + (Bolt/Weld x W4) + (Cleanup x W5)
                      </div>
                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#166534' }}>Default weights (configurable via Rules of Credit):</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <span style={{ padding: '4px 10px', backgroundColor: '#dcfce7', borderRadius: '4px', fontSize: '12px' }}>Transport: 15%</span>
                        <span style={{ padding: '4px 10px', backgroundColor: '#dcfce7', borderRadius: '4px', fontSize: '12px' }}>Erect Supports: 20%</span>
                        <span style={{ padding: '4px 10px', backgroundColor: '#dcfce7', borderRadius: '4px', fontSize: '12px' }}>Fit-Up: 10%</span>
                        <span style={{ padding: '4px 10px', backgroundColor: '#dcfce7', borderRadius: '4px', fontSize: '12px' }}>Bolt/Weld: 35%</span>
                        <span style={{ padding: '4px 10px', backgroundColor: '#dcfce7', borderRadius: '4px', fontSize: '12px' }}>Line Cleanup: 20%</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Earned Hours (Piping)</h3>
                    <div style={{ padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>Shop Earned Hours:</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#dbeafe', padding: '4px 8px', borderRadius: '4px' }}>
                          Budget Hours x (Shop/Field Ratio / 100) x (Total Shop Completion / 100)
                        </code>
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>Field Earned Hours:</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#dbeafe', padding: '4px 8px', borderRadius: '4px' }}>
                          Budget Hours x (1 - Shop/Field Ratio / 100) x (Total Field Completion / 100)
                        </code>
                      </div>
                      <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#3b82f6' }}>
                        Total Earned Hours = Shop Earned + Field Earned. The ratio splits budget hours between shop and field.
                        Field Run lines earn 0 shop hours and are excluded from overall fabrication progress. Shop = Site counts as on-site fabrication.
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Earned Credit (Piping)</h3>
                    <div style={{ padding: '20px', backgroundColor: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: '600', color: '#7c3aed', marginBottom: '4px' }}>Shop Earned Credit:</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#ede9fe', padding: '4px 8px', borderRadius: '4px' }}>
                          Credit x (Shop/Field Ratio / 100) x (Total Shop Completion / 100)
                        </code>
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#7c3aed', marginBottom: '4px' }}>Field Earned Credit:</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#ede9fe', padding: '4px 8px', borderRadius: '4px' }}>
                          Credit x (1 - Shop/Field Ratio / 100) x (Total Field Completion / 100)
                        </code>
                      </div>
                      <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#7c3aed' }}>
                        Total Earned Credit = Shop Earned Credit + Field Earned Credit. Uses Credit (from sqrt formula) instead of Budget Hours.
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Small Bore (SB) vs Large Bore (LB)</h3>
                    <div style={{ padding: '20px', backgroundColor: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                      <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#9a3412' }}>
                        The <strong>Bore</strong> column is auto-calculated from the pipe size. The system parses the Size column 
                        (including fractions like 1/2", 3/4", 1 1/2") and classifies based on the <strong>2-inch threshold</strong>:
                      </p>
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
                        <div style={{ flex: 1, minWidth: '200px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px' }}>
                          <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '4px' }}>Small Bore (SB)</div>
                          <div style={{ fontSize: '12px', color: '#78350f' }}>Size &le; 2" (e.g., 1/2", 3/4", 1", 1 1/2", 2"). Footage goes into <code>SB Ft</code> and <code>SB Ft Installed</code>.</div>
                        </div>
                        <div style={{ flex: 1, minWidth: '200px', padding: '12px', backgroundColor: '#dbeafe', borderRadius: '6px' }}>
                          <div style={{ fontWeight: '700', color: '#1e40af', marginBottom: '4px' }}>Large Bore (LB)</div>
                          <div style={{ fontSize: '12px', color: '#1e3a5f' }}>Size &gt; 2" (e.g., 3", 4", 6", 8", 12", 14"+). Footage goes into <code>LB Ft</code> and <code>LB Ft Installed</code>.</div>
                        </div>
                      </div>
                      <p style={{ margin: '0', fontSize: '12px', color: '#9a3412' }}>
                        The <strong>Mat-Bore</strong> column combines material type and bore (e.g., "CS LB" = Carbon Steel Large Bore). 
                        All footage and bore columns are auto-calculated from Size and Length — they are not imported.
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Footage Analytics Formulas</h3>
                    <div style={{ padding: '20px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>Total Footage (auto-calculated per row):</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px' }}>
                          SB Ft = sum of Length where Size &le; 2"
                        </code>
                        <br style={{ marginBottom: '4px' }} />
                        <code style={{ fontSize: '13px', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '4px' }}>
                          LB Ft = sum of Length where Size &gt; 2"
                        </code>
                        <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#065f46' }}>
                          Supports fractions: 1/2" = 0.5", 3/4" = 0.75", 1 1/2" = 1.5". Each row can have up to 3 sizes/lengths.
                        </p>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>Fabricated Footage (auto-calculated per row):</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px' }}>
                          SB Ft Installed = SB Ft x (Shop Completion / 100)
                        </code>
                        <br style={{ marginBottom: '4px' }} />
                        <code style={{ fontSize: '13px', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '4px' }}>
                          LB Ft Installed = LB Ft x (Shop Completion / 100)
                        </code>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>Installed Footage — Quantity tab (analytics):</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px' }}>
                          SB: SB Ft x (Total Field Completion / 100)
                        </code>
                        <br style={{ marginBottom: '4px' }} />
                        <code style={{ fontSize: '13px', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '4px' }}>
                          LB: LB Ft x (Total Field Completion / 100)
                        </code>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>Installed Footage — Quantity 2 tab (analytics):</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#bbf7d0', padding: '4px 8px', borderRadius: '4px' }}>
                          SB: SB Ft x (Transport to Installation Area / 100)
                        </code>
                        <br />
                        <code style={{ fontSize: '13px', backgroundColor: '#bbf7d0', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '4px' }}>
                          LB: LB Ft x (Transport to Installation Area / 100)
                        </code>
                        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#065f46' }}>
                          Quantity 2 uses only the <strong>Transportation</strong> step percentage instead of total field completion.
                        </p>
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>Remaining Footage (per row):</div>
                        <code style={{ fontSize: '13px', backgroundColor: '#d1fae5', padding: '4px 8px', borderRadius: '4px' }}>
                          Total Footage - Installed Footage
                        </code>
                      </div>
                    </div>
                  </div>
                </>)}

                {disciplineId !== 'piping' && disciplineId !== 'piping-nde' && (<>
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Credit</h3>
                    <div style={{ padding: '20px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '2px solid #f59e0b' }}>
                      <p style={{ margin: '0', fontSize: '13px', color: '#78350f' }}>
                        For <strong>{config.name}</strong>, Credit is a <strong>manually entered</strong> value that represents the weight/complexity 
                        of a line item. It is used to calculate Earned Credit. Enter the budget credit value for each item to enable earned credit calculations.
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Total Field Completion</h3>
                    <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '600', color: '#15803d', marginBottom: '12px' }}>
                        Total Field Completion = Sum of (each Progress Step % x its Weight)
                      </div>
                      <p style={{ margin: '0', fontSize: '13px', color: '#166534' }}>
                        Each progress step column has a weight (configured via Rules of Credit). The Total Field Completion is the 
                        weighted sum of all progress steps. This percentage drives the earned calculations.
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Earned Hours</h3>
                    <div style={{ padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <code style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
                        Earned Hours = Budget Hours x (Total Field Completion / 100)
                      </code>
                      <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#3b82f6' }}>
                        As Total Field Completion increases from 0% to 100%, earned hours grow proportionally from 0 to Budget Hours.
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Earned Credit</h3>
                    <div style={{ padding: '20px', backgroundColor: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                      <code style={{ fontSize: '14px', fontWeight: '600', color: '#7c3aed' }}>
                        Earned Credit = Credit x (Total Field Completion / 100)
                      </code>
                      <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#7c3aed' }}>
                        As Total Field Completion increases from 0% to 100%, earned credit grows proportionally from 0 to Credit.
                      </p>
                    </div>
                  </div>
                </>)}

                {/* Calculation Flow Diagram */}
                <div>
                  <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem' }}>Calculation Flow</h3>
                  <div style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                      {disciplineId === 'piping' && (<>
                        <span style={{ padding: '6px 12px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '12px', color: '#1e40af' }}>Size x Length</span>
                        <span style={{ color: '#9ca3af' }}>-&gt;</span>
                        <span style={{ padding: '6px 12px', backgroundColor: '#fef3c7', borderRadius: '4px', fontSize: '12px', color: '#92400e', fontWeight: '600' }}>Credit (sqrt)</span>
                        <span style={{ color: '#9ca3af' }}>+</span>
                      </>)}
                      <span style={{ padding: '6px 12px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '12px', color: '#1e40af' }}>Progress Steps</span>
                      <span style={{ color: '#9ca3af' }}>-&gt;</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#dcfce7', borderRadius: '4px', fontSize: '12px', color: '#15803d', fontWeight: '600' }}>Total Field Completion</span>
                      <span style={{ color: '#9ca3af' }}>-&gt;</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#e0e7ff', borderRadius: '4px', fontSize: '12px', color: '#4338ca', fontWeight: '600' }}>Earned Hours</span>
                      <span style={{ color: '#9ca3af' }}>&amp;</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#fae8ff', borderRadius: '4px', fontSize: '12px', color: '#a21caf', fontWeight: '600' }}>Earned Credit</span>
                      <span style={{ color: '#9ca3af' }}>-&gt;</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#fee2e2', borderRadius: '4px', fontSize: '12px', color: '#b91c1c', fontWeight: '600' }}>Reports (Earned, Budget, EVM, SOV)</span>
                    </div>
                  </div>
                </div>
              </>)}

              {/* ========== WORKFLOW TAB ========== */}
              {helpTab === 'workflow' && (<>
                <h3 style={{ margin: '0 0 24px 0', textAlign: 'center', color: '#111827' }}>{config.name} Progress Tracking Workflow</h3>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div style={{ padding: '16px 32px', backgroundColor: '#dbeafe', borderRadius: '8px', border: '2px solid #3b82f6', textAlign: 'center', minWidth: '320px' }}>
                    <div style={{ fontWeight: '600', color: '#1e40af' }}>1. Import Data (Excel Upload)</div>
                    <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>
                      {disciplineId === 'piping'
                        ? 'Line items, sizes, lengths, materials, budget hours, shop/field ratio'
                        : 'Line items, descriptions, budget hours, credit values'
                      }
                    </div>
                  </div>

                  <div style={{ fontSize: '24px', color: '#9ca3af' }}>
                    {disciplineId === 'piping' ? '↓ Credit auto-calculated from sizes/lengths' : '↓'}
                  </div>

                  {disciplineId === 'piping' && (<>
                    <div style={{ padding: '16px 32px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '2px solid #f59e0b', textAlign: 'center', minWidth: '320px' }}>
                      <div style={{ fontWeight: '600', color: '#b45309' }}>2. Credit Auto-Calculated</div>
                      <div style={{ fontSize: '12px', color: '#d97706', marginTop: '4px' }}>sqrt(S1xL1) + sqrt(S2xL2) + sqrt(S3xL3) = Budget Credit per line</div>
                    </div>
                    <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓</div>
                  </>)}

                  <div style={{ padding: '16px 32px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '2px solid #f59e0b', textAlign: 'center', minWidth: '320px' }}>
                    <div style={{ fontWeight: '600', color: '#b45309' }}>{disciplineId === 'piping' ? '3' : '2'}. Update Progress (Manual)</div>
                    <div style={{ fontSize: '12px', color: '#d97706', marginTop: '4px' }}>
                      {disciplineId === 'piping'
                        ? 'Set shop completion + field steps: Transport, Erect, Fit-Up, Bolt/Weld, Cleanup'
                        : 'Set each progress step to 0 / 25 / 50 / 75 / 100%'
                      }
                    </div>
                  </div>

                  <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓ Database auto-calculates instantly</div>

                  <div style={{ padding: '16px 32px', backgroundColor: '#dcfce7', borderRadius: '8px', border: '2px solid #22c55e', textAlign: 'center', minWidth: '320px' }}>
                    <div style={{ fontWeight: '600', color: '#15803d' }}>{disciplineId === 'piping' ? '4' : '3'}. Auto-Calculated Results</div>
                    <div style={{ fontSize: '12px', color: '#16a34a', marginTop: '4px' }}>Total Field Completion, Earned Hours, Earned Credit</div>
                  </div>

                  <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓ Snapshots saved periodically</div>

                  <div style={{ padding: '16px 32px', backgroundColor: '#e0e7ff', borderRadius: '8px', border: '2px solid #6366f1', textAlign: 'center', minWidth: '320px' }}>
                    <div style={{ fontWeight: '600', color: '#4338ca' }}>{disciplineId === 'piping' ? '5' : '4'}. History Snapshots</div>
                    <div style={{ fontSize: '12px', color: '#6366f1', marginTop: '4px' }}>Periodic snapshots store earned hours, earned credit, and completions over time</div>
                  </div>

                  <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓</div>

                  <div style={{ padding: '16px 32px', backgroundColor: '#fae8ff', borderRadius: '8px', border: '2px solid #c026d3', textAlign: 'center', minWidth: '320px' }}>
                    <div style={{ fontWeight: '600', color: '#a21caf' }}>{disciplineId === 'piping' ? '6' : '5'}. Reports & Analytics</div>
                    <div style={{ fontSize: '12px', color: '#c026d3', marginTop: '4px' }}>Earned Report (hours + credit over time), Budget Report, EVM, SOV, Analytics Dashboard</div>
                  </div>
                </div>

                {/* Data flow summary */}
                <div style={{ marginTop: '32px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>Data Flow Summary</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px' }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#15803d', marginBottom: '4px' }}>Input (You Provide):</div>
                      <ul style={{ margin: 0, paddingLeft: '16px', color: '#374151' }}>
                        <li>Excel import: line items, dimensions, budget</li>
                        <li>Progress steps: 0/25/50/75/100%</li>
                        {disciplineId === 'piping' && <li>Shop completion percentage</li>}
                        {disciplineId !== 'piping' && disciplineId !== 'piping-nde' && <li>Credit value (manual)</li>}
                      </ul>
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Output (Auto-Calculated):</div>
                      <ul style={{ margin: 0, paddingLeft: '16px', color: '#374151' }}>
                        {disciplineId === 'piping' && <li>Credit (sqrt formula from dimensions)</li>}
                        <li>Total Field Completion (weighted progress)</li>
                        <li>Earned Hours (budget x completion)</li>
                        <li>Earned Credit (credit x completion)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>)}

              {/* ========== VIDEO TOUR — peer tab, last ========== */}
              {helpTab === 'tour' && (disciplineId === 'piping' || disciplineId === 'piping-nde') && (
                <PipingProgressWalkthrough showHeader compact />
              )}
              {helpTab === 'tour' && disciplineId === 'piping-testing' && (
                <HelpVideoTour
                  guideId="pipe-testing-tracker-help"
                  title="Testing Tracker — Video Tour"
                  accent="#0f766e"
                  compact
                  showHeader
                />
              )}
              {helpTab === 'tour' && disciplineId !== 'piping' && disciplineId !== 'piping-nde' && disciplineId !== 'piping-testing' && (
                <HelpVideoTour
                  guideId="discipline-matrix-help"
                  title={`${config.name} Progress Tracker — Video Tour`}
                  accent="#0369a1"
                  compact
                  showHeader
                />
              )}

            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .virtualized-table-container { scrollbar-width: thin; scrollbar-color: #c1c1c1 #f1f1f1; }
        .virtualized-table-container::-webkit-scrollbar { width: 12px; height: 12px; }
        .virtualized-table-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
        .virtualized-table-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .virtualized-table-container::-webkit-scrollbar-thumb:hover { background: #a1a1a1; }
        .virtualized-table-container tbody tr:hover { background-color: #e8f4fc !important; }
      `}</style>
    </div>
  );
};

export default DisciplineMatrix;